import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useContent } from "../lib/content";
import { TIMELINE_STAGES, BUYER_DATE_FIELDS } from "../lib/data";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Plus, Trash2, Save, ClipboardList, MessageSquare, Calendar, Briefcase, FileCheck2, Eye, FileSignature, GitCompareArrows, CalendarClock, FileText, Calculator, FolderArchive, Upload as UploadIcon, Send, Bell, ChevronDown, ChevronUp, Heart, Home, Phone, Route, RotateCcw, CheckSquare, TrendingUp, Share2, Sparkles, Copy, RefreshCw, ImagePlus, MessagesSquare, Globe, Printer, DoorOpen } from "lucide-react";
import ListingInputForm from "../components/forms/ListingInputForm";
import { buildRenderedSections } from "../lib/listingSchema";
import { SectionedFormEditor } from "../components/admin/SectionedFormEditor";
import { ChecklistEditor } from "../components/admin/ChecklistEditor";
import { DEFAULT_CONTENT } from "../lib/content";
import { normalizeActivityStats, newPlatformRow, isAutoStat, isLockedStat, STAT_FIELDS as ACTIVITY_STAT_FIELDS } from "../lib/activityStats";
import CampaignBuilder from "../components/admin/CampaignBuilder";
import ListingSiteTab from "../components/admin/ListingSiteTab";
import PrintTab from "../components/admin/PrintTab";
import { ClientFormsTab } from "../components/admin/FormLibrary";

// Listing lifecycle (mirrored on AdminSellers list).
const LIFECYCLE = [
  { id: "pre_listing",    label: "Pre-listing",    color: "#B85C47" },
  { id: "active",         label: "Active",         color: "#2B7A4B" },
  { id: "under_contract", label: "Under contract", color: "#5C4FAA" },
  { id: "closed",         label: "Closed",         color: "#78716C" },
];
const lifecycleLabel = (id) => (LIFECYCLE.find((l) => l.id === id) || LIFECYCLE[1]);

const TABS = [
  { id: "overview", label: "Overview", icon: <Home size={14} />, sides: ["sell", "buy"] },
  { id: "tasks", label: "Custom Tasks", icon: <ClipboardList size={14} />, sides: ["sell"] },
  { id: "note", label: "Agent Note", icon: <MessageSquare size={14} />, sides: ["sell", "buy"] },
  { id: "dates", label: "Key Dates", icon: <Calendar size={14} />, sides: ["sell", "buy"] },
  { id: "under_contract", label: "Under Contract", icon: <FileSignature size={14} />, sides: ["sell"] },
  { id: "buyer_info", label: "Buyer Welcome", icon: <FileText size={14} />, sides: ["sell"] },
  { id: "offers", label: "Offers", icon: <GitCompareArrows size={14} />, sides: ["sell"] },
  { id: "open_houses", label: "Open Houses", icon: <DoorOpen size={14} />, sides: ["sell"] },
  { id: "showings", label: "Showings", icon: <CalendarClock size={14} />, sides: ["sell"] },
  { id: "feedback", label: "Buyer Feedback", icon: <MessagesSquare size={14} />, sides: ["sell"] },
  { id: "activity", label: "Activity", icon: <TrendingUp size={14} />, sides: ["sell"] },
  { id: "social", label: "Social Posts", icon: <Share2 size={14} />, sides: ["sell"] },
  { id: "listing_site", label: "Listing Page", icon: <Globe size={14} />, sides: ["sell"] },
  { id: "print", label: "Print", icon: <Printer size={14} />, sides: ["sell"] },
  { id: "vendors", label: "Vendors", icon: <Briefcase size={14} />, sides: ["sell", "buy"] },
  { id: "wishlist", label: "Wishlist", icon: <Heart size={14} />, sides: ["buy"] },
  { id: "buyer_lender", label: "Lender", icon: <Phone size={14} />, sides: ["buy"] },
  { id: "buyer_process", label: "Process", icon: <Route size={14} />, sides: ["buy"] },
  { id: "buyer_intake", label: "Intake Forms", icon: <ClipboardList size={14} />, sides: ["buy"] },
  { id: "buyer_closing", label: "Closing", icon: <CheckSquare size={14} />, sides: ["buy"] },
  { id: "net_sheet", label: "Net Sheet", icon: <Calculator size={14} />, sides: ["sell"] },
  { id: "listing_form", label: "Listing Form", icon: <FileText size={14} />, sides: ["sell"] },
  { id: "documents", label: "Documents", icon: <FolderArchive size={14} />, sides: ["sell"] },
  { id: "submissions", label: "Submissions", icon: <FileCheck2 size={14} />, sides: ["sell"] },
  { id: "client_forms", label: "Forms", icon: <FileText size={14} />, sides: ["sell", "buy"] },
];

// Which heading each tab sits under, per side. TABS above stays the source
// of truth for label, icon and side; this only says where a tab lives and
// in what order. Anything missing from here still renders -- it falls into
// "More" -- so adding a tab to TABS can never make it disappear.
//
// `phase: "late"` marks a group that only starts mattering once the
// listing is under contract. It is dimmed until then, never hidden.
const RAIL_GROUPS = {
  sell: [
    { name: "", ids: ["overview", "note", "dates"] },
    { name: "Prep", ids: ["listing_form", "tasks", "vendors", "documents", "submissions", "client_forms"] },
    { name: "Market", ids: ["listing_site", "print", "social", "activity"] },
    { name: "Sell", ids: ["open_houses", "showings", "feedback", "offers"] },
    { name: "Close", ids: ["under_contract", "net_sheet", "buyer_info"], phase: "late" },
  ],
  buy: [
    { name: "", ids: ["overview", "note"] },
    { name: "Setup", ids: ["buyer_intake", "buyer_lender", "wishlist", "vendors", "client_forms"] },
    { name: "Search", ids: ["buyer_process"] },
    { name: "Close", ids: ["dates", "buyer_closing"] },
  ],
};

/* The tab bar, as a grouped rail.

   Two wrapped rows of pills left the group names nowhere to live, so
   seventeen destinations read as one flat list of everything that exists.
   Vertical has room for headings, and the headings are the actual fix:
   they stop Print sitting beside Vendors as though the two were the same
   kind of thing.

   data-testid on each button is unchanged from the pill version on
   purpose -- the existing tests address tabs by `tab-${id}`. */
function SellerRail({ tabs, groups, active, onPick, counts = {}, dimLate = false }) {
  const byId = new Map(tabs.map((t) => [t.id, t]));
  const used = new Set();
  const blocks = groups
    .map((g) => {
      const items = g.ids.map((id) => byId.get(id)).filter(Boolean);
      items.forEach((t) => used.add(t.id));
      return { ...g, items };
    })
    .filter((g) => g.items.length);
  const leftover = tabs.filter((t) => !used.has(t.id));
  if (leftover.length) blocks.push({ name: "More", items: leftover });

  return (
    <nav data-testid="seller-rail" className="seller-rail">
      {blocks.map((g, gi) => {
        const dim = dimLate && g.phase === "late";
        return (
          <div
            key={g.name || `g${gi}`}
            className="rail-group"
            style={{ opacity: dim ? 0.45 : 1 }}
          >
            {g.name && (
              <div style={{ padding: "0 16px 5px", fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>
                {g.name}
                {dim && (
                  <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400, fontStyle: "italic", marginLeft: 6 }}>not yet</span>
                )}
              </div>
            )}
            {g.items.map((t) => {
              const on = active === t.id;
              const n = counts[t.id];
              return (
                <button
                  key={t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => onPick(t.id)}
                  className="btn-ghost"
                  style={{
                    display: "flex", alignItems: "center", gap: 9,
                    width: "100%", textAlign: "left",
                    padding: "7px 14px 7px 13px", borderRadius: 0,
                    borderLeft: `3px solid ${on ? "var(--primary)" : "transparent"}`,
                    background: on ? "var(--surface-alt)" : "transparent",
                    color: on ? "var(--primary)" : "var(--text-muted)",
                    fontSize: 13, fontWeight: on ? 600 : 500,
                  }}
                >
                  {t.icon}
                  <span style={{ flex: "1 1 auto" }}>{t.label}</span>
                  {n > 0 && (
                    <span style={{ fontSize: 10.5, fontWeight: 600, lineHeight: 1, padding: "4px 6px", borderRadius: 999, background: "var(--border)", color: "var(--text-muted)" }}>
                      {n}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}

/* ---------- Overview ---------- */

// The page already listens for this; DatesTab uses it for its shortcut link.
const jumpToTab = (id) =>
  window.dispatchEvent(new CustomEvent("admin-jump-to-tab", { detail: id }));

// Whole days from today. Parsed at local midnight rather than with
// new Date("2026-10-03"), which JS reads as UTC and so lands a day early
// for anyone west of Greenwich -- which is everyone using this.
function daysFromToday(iso) {
  if (!iso) return null;
  const d = new Date(String(iso) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

function whenLabel(n) {
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n === -1) return "yesterday";
  return n > 0 ? n + " days" : Math.abs(n) + " days ago";
}

// Both the step itself and the no-dates card behind it ask this, and they
// must never disagree: a next step sitting on top of an empty-dates
// complaint is exactly the pile-up this was meant to clear.
function nextStepNeeded(seller, outstanding) {
  return (seller?.status || "") === "pending" || (outstanding || []).length > 0;
}

// The one next step, in place of three cards that each report an absence.
// A client who has never opened their portal cannot fill anything in, so
// that invite is the whole job; after that it is whichever forms are still
// out. When neither applies this renders nothing and the cards below carry
// the page on their own - an empty state should tell an agent what to do,
// not count up the things that have not happened yet.
function NextStep({ seller, outstanding }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const waiting = (seller?.status || "") === "pending";
  const left = outstanding || [];

  if (!nextStepNeeded(seller, outstanding)) return null;

  const resend = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post(`/admin/sellers/${seller._id}/resend-invite`);
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const first = String(seller?.name || "").trim().split(" ")[0] || "They";
  return (
    <div
      className="card"
      data-testid="overview-next-step"
      style={{
        padding: "16px 18px",
        borderLeft: "3px solid var(--primary)",
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: "1 1 240px", minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>
          {waiting
            ? first + " hasn't opened their portal yet."
            : left.length + (left.length === 1 ? " form" : " forms") + " still to come back."}
        </div>
        <div style={{ color: "var(--text-muted)", fontSize: 12.5, marginTop: 2 }}>
          {waiting
            ? "Nothing else can start until they set their password."
            : left.map((k) => REMINDER_FORM_LABELS[k] || k).join(", ")}
        </div>
        {err ? (
          <div style={{ color: "var(--error)", fontSize: 12.5, marginTop: 4 }}>{err}</div>
        ) : null}
      </div>
      {waiting ? (
        <button type="button" className="btn-secondary" disabled={busy || sent} onClick={resend}>
          {sent ? "Link sent" : busy ? "Sending..." : "Send their link again"}
        </button>
      ) : (
        <button type="button" className="btn-secondary" onClick={() => jumpToTab("submissions")}>
          Nudge them &rarr;
        </button>
      )}
    </div>
  );
}

function OverviewCard({ title, children, onClick, cta }) {
  return (
    <div className="card" style={{ padding: "16px 18px" }}>
      <div className="overline" style={{ marginBottom: 8 }}>{title}</div>
      {children}
      {cta && (
        <button type="button" className="btn-ghost" onClick={onClick}
                style={{ marginTop: 10, padding: 0, fontSize: 13, color: "var(--primary)" }}>
          {cta} &rarr;
        </button>
      )}
    </div>
  );
}

/* Where this client is at, on one screen.

   Everything here is read out of the personalization doc and the
   submissions list the page already fetched, so landing here costs no
   extra request. That is the whole reason it can be the default tab.
   Each card is a way in to the tab that actually owns the thing. */
function OverviewTab({ p, seller, submissions, isBuySide }) {
  const { content } = useContent();

  // Same field resolution DatesTab uses, so the labels match what she set
  // in Content rather than the hard-coded fallbacks.
  const baseFields = isBuySide
    ? BUYER_DATE_FIELDS
    : ((content.date_fields && content.date_fields.length > 0) ? content.date_fields : DATE_FIELDS);
  const hidden = p.hidden_date_fields || [];
  const fields = [...baseFields, ...(p.custom_date_fields || [])]
    .filter((f) => f && f.key && !hidden.includes(f.key));

  const keyDates = p.key_dates || {};
  const keyTimes = p.key_dates_times || {};
  const dated = fields
    .map((f) => ({ label: f.label, key: f.key, value: keyDates[f.key], days: daysFromToday(keyDates[f.key]) }))
    .filter((f) => f.days !== null);
  const upcoming = dated.filter((f) => f.days >= 0).sort((a, b) => a.days - b.days);
  const next = upcoming[0] || null;
  const soon = upcoming.slice(1, 4);

  const tasks = p.custom_tasks || [];
  const overdue = tasks.filter((t) => t.due_date && daysFromToday(t.due_date) < 0);

  const offers = p.offers || [];
  const prices = offers
    .map((o) => parseFloat(o.purchase_price))
    .filter((n) => !isNaN(n) && n > 0)
    .sort((a, b) => b - a);

  const submitted = new Set((submissions || []).map((s) => s.form_type));
  const outstanding = isBuySide ? [] : REMINDER_FORM_KEYS.filter((k) => !submitted.has(k));
  // The complement of `outstanding`: the forms that have actually come
  // back. The banner at the top of the Overview column already names the
  // ones still missing, and printing the same three labels again in the
  // Paperwork card made one fact look like two.
  const submittedLabels = isBuySide
    ? []
    : REMINDER_FORM_KEYS.filter((k) => submitted.has(k)).map(
        (k) => REMINDER_FORM_LABELS[k] || k
      );

  const note = (p.agent_note || "").trim();
  const muted = { color: "var(--text-muted)" };
  // This card was printing the raw stored values ("2026-10-11 - 13:00").
  const prettyDate = (iso) => {
    const d = new Date(String(iso || "") + "T00:00:00");
    return Number.isNaN(d.getTime()) ? String(iso || "")
      : d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  };
  const prettyTime = (t) => {
    const s = String(t || "").trim();
    if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) return s;
    const [hh, mm] = s.split(":");
    const hr = parseInt(hh, 10);
    return ((hr + 11) % 12 + 1) + ":" + mm + " " + (hr >= 12 ? "PM" : "AM");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 760 }}>
      <NextStep seller={seller} outstanding={outstanding} />


      {next ? (
        <div className="card" data-testid="overview-next"
             style={{ padding: "16px 18px", borderLeft: "3px solid var(--secondary)",
                      display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div className="font-serif" style={{ fontSize: 26, fontWeight: 600, color: "var(--secondary)", lineHeight: 1, whiteSpace: "nowrap" }}>
            {whenLabel(next.days)}
          </div>
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>{next.label}</div>
            <div style={{ ...muted, fontSize: 12.5, marginTop: 2 }}>
              {prettyDate(next.value)}{keyTimes[next.key] ? " · " + prettyTime(keyTimes[next.key]) : ""}
              {soon.length > 0 && " \u00b7 then " + soon.map((s) => s.label).join(", ")}
            </div>
          </div>
          <button type="button" className="btn-ghost" onClick={() => jumpToTab("dates")}
                  style={{ fontSize: 13, color: "var(--primary)" }}>All dates &rarr;</button>
        </div>
      ) : nextStepNeeded(seller, outstanding) ? null : (
        <div className="card" data-testid="overview-next" style={{ padding: "16px 18px", ...muted }}>
          No upcoming dates set.{" "}
          <button type="button" className="btn-ghost" onClick={() => jumpToTab("dates")}
                  style={{ padding: 0, fontSize: 13, color: "var(--primary)" }}>Add some &rarr;</button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, alignItems: "start" }}>

        <OverviewCard title="Your note" cta={note ? "Edit" : "Write one"} onClick={() => jumpToTab("note")}>
          {note ? (
            <p className="font-serif" style={{ margin: 0, fontSize: 16, lineHeight: 1.45 }}>
              {note.length > 220 ? note.slice(0, 220) + "\u2026" : note}
            </p>
          ) : (
            <p style={{ margin: 0, ...muted, fontSize: 13 }}>
              Nothing yet. This is the bit you want in front of you before you call them.
            </p>
          )}
        </OverviewCard>

        {!isBuySide && (
          <OverviewCard title="Paperwork" cta="Submissions" onClick={() => jumpToTab("submissions")}>
            <div style={{ fontSize: 14 }}>
              <b style={{ fontWeight: 600 }}>{REMINDER_FORM_KEYS.length - outstanding.length}</b>
              <span style={muted}> of {REMINDER_FORM_KEYS.length} forms in</span>
            </div>
            {submittedLabels.length > 0 ? (
              <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none", fontSize: 12.5, ...muted }}>
                {submittedLabels.map((l) => (
                  <li key={l} style={{ padding: "2px 0" }}>&middot; {l}</li>
                ))}
              </ul>
            ) : (
              <p style={{ margin: "8px 0 0", fontSize: 12.5, ...muted }}>Nothing back yet.</p>
            )}
          </OverviewCard>
        )}

        {!isBuySide && (
          <OverviewCard title="Prep tasks" cta="Tasks" onClick={() => jumpToTab("tasks")}>
            <div style={{ fontSize: 14 }}>
              <b style={{ fontWeight: 600 }}>{tasks.length}</b>
              <span style={muted}> custom {tasks.length === 1 ? "task" : "tasks"}</span>
            </div>
            <p style={{ margin: "8px 0 0", fontSize: 12.5, color: overdue.length ? "var(--secondary)" : "var(--text-muted)" }}>
              {overdue.length > 0
                ? overdue.length + " past its due date"
                : (tasks.length ? "Nothing overdue." : "None added yet.")}
            </p>
          </OverviewCard>
        )}

        {!isBuySide && prices.length > 0 && (
          <OverviewCard title="Offers" cta="Compare" onClick={() => jumpToTab("offers")}>
            <div className="font-serif" style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.1 }}>
              ${prices[0].toLocaleString()}
            </div>
            <p style={{ margin: "6px 0 0", fontSize: 12.5, ...muted }}>
              highest of {prices.length} {prices.length === 1 ? "offer" : "offers"}
            </p>
          </OverviewCard>
        )}

      </div>
    </div>
  );
}

// Under-contract fields are now CMS-managed at content.under_contract_fields
// (Admin → Content → Date types → Under-contract block). See lib/content.jsx.



import { PREP_CATEGORIES } from "../lib/data";

const CATS = [
  ...PREP_CATEGORIES,
  { id: "custom", label: "Custom Category" },
];

const DATE_FIELDS = [
  { key: "strategy_meeting", label: "Strategy Meeting" },
  { key: "staging", label: "Staging" },
  { key: "cleaners", label: "Cleaners" },
  { key: "photo_day", label: "Photo / Video Day" },
  { key: "listing_launch", label: "Listing Launch" },
  { key: "open_house", label: "Open House" },
  { key: "closing", label: "Closing" },
];

export default function AdminSellerDetail() {
  const { id } = useParams();
  const { impersonate, user: currentUser } = useAuth();
  const buyerFeaturesEnabled = !!currentUser?.buyer_features_enabled;
  const nav = useNavigate();
  const [seller, setSeller] = useState(null);
  const [p, setP] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [tab, setTab] = useState("overview");
  const [toast, setToast] = useState("");
  // Which side of the client we're editing when they have both. Loaded
  // from ?side=buy on the URL (persists across refresh + link-sharing)
  // or defaults to whichever household_id the backend resolves.
  const searchParams = new URLSearchParams(window.location.search);
  const [editSide, setEditSide] = useState(searchParams.get("side") || "");
  const isBuySide = editSide === "buy" || (seller?.active_side_for_edit === "buy" && !editSide);

  const viewAsSeller = async () => {
    const res = await impersonate(id);
    if (res.ok) nav("/dashboard");
    else setToast(res.error || "Could not start view-as-seller");
  };

  const load = async () => {
    const q = editSide ? `?side=${editSide}` : "";
    const [per, sub] = await Promise.all([
      api.get(`/admin/seller/${id}/personalization${q}`),
      api.get(`/admin/seller/${id}/submissions`),
    ]);
    setSeller(per.data.seller);
    setP(per.data.personalization);
    setSubmissions(sub.data.submissions || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, editSide]);

  // When the edit side changes (e.g. admin flips SELLING → BUYING), snap the
  // active tab to one that's actually valid for the new side. Otherwise a
  // buy-only client opens on "tasks" which is a sell-only tab and the tab
  // bar renders nothing selected — the whole reason a buyer-personalize
  // page looked identical to a seller's.
  useEffect(() => {
    const validForSide = TABS.filter((t) => t.sides.includes(isBuySide ? "buy" : "sell")).map((t) => t.id);
    if (validForSide.length && !validForSide.includes(tab)) {
      setTab(validForSide[0]);
    }
  }, [isBuySide, tab]);

  // Allow nested components (e.g. shortcut link from DatesTab) to switch the active tab.
  useEffect(() => {
    const handler = (e) => { if (e?.detail) setTab(e.detail); };
    window.addEventListener("admin-jump-to-tab", handler);
    return () => window.removeEventListener("admin-jump-to-tab", handler);
  }, []);

  const save = async (patch, msg = "Saved.") => {
    try {
      const q = editSide ? `?side=${editSide}` : "";
      const { data } = await api.put(`/admin/seller/${id}/personalization${q}`, patch);
      setP(data.personalization);
      setToast(msg);
      setTimeout(() => setToast(""), 2500);
    } catch (e) {
      setToast(formatApiError(e.response?.data?.detail) || "Save failed");
      setTimeout(() => setToast(""), 3500);
    }
  };

  const setLifecycle = async (value) => {
    const prev = seller;
    setSeller((s) => ({ ...s, lifecycle_status: value }));
    try {
      await api.patch(`/admin/sellers/${id}/lifecycle-status`, { lifecycle_status: value });
      setToast(`Status updated to ${lifecycleLabel(value).label}.`);
      setTimeout(() => setToast(""), 2200);
    } catch (e) {
      setSeller(prev);
      setToast(formatApiError(e.response?.data?.detail) || "Status update failed.");
      setTimeout(() => setToast(""), 3500);
    }
  };

  // Derive the client's current transaction type from their linked sides.
  const currentType = (() => {
    const sides = Array.isArray(seller?.sides) ? seller.sides : ["sell"];
    if (sides.length > 1) return "both";
    if (sides[0] === "buy") return "buy";
    return "sell";
  })();

  // Change whether this client is a seller, buyer, or both. Adds/removes
  // portal sides without deleting data — see backend transaction-type route.
  const [typeBusy, setTypeBusy] = useState(false);
  const changeType = async (newType) => {
    if (newType === currentType || typeBusy) return;
    setTypeBusy(true);
    try {
      const { data } = await api.patch(`/admin/sellers/${id}/transaction-type`, { transaction_type: newType });
      const label = newType === "both" ? "Buyer + Seller" : (newType === "buy" ? "Buyer" : "Seller");
      setToast(`Client type updated to ${label}.`);
      setTimeout(() => setToast(""), 2800);
      // If they now have a buy side and didn't before, jump the editor to it
      // so the buyer tabs (Note, Key Dates, Vendors, Wishlist) are right there.
      const newSide = newType === "buy" ? "buy" : (newType === "both" && currentType === "sell" ? "buy" : "");
      if (newSide && newSide !== editSide) {
        setEditSide(newSide);
      } else {
        await load();
      }
    } catch (e) {
      setToast(formatApiError(e.response?.data?.detail) || "Couldn't update client type.");
      setTimeout(() => setToast(""), 4000);
    } finally {
      setTypeBusy(false);
    }
  };

  if (!seller || !p) {
    return <div className="container-x" style={{ padding: "48px 0", color: "var(--text-muted)" }}>Loading…</div>;
  }

  // Counts the page already holds, so the rail costs no extra request.
  // Showings, feedback and documents each fetch inside their own tab, so
  // their numbers are left off rather than guessed at from here.
  const railCounts = {
    tasks: (p.custom_tasks || []).length,
    offers: (p.offers || []).length,
    submissions: submissions.length,
  };

  // The closing group is dimmed, not hidden, until the listing gets there.
  // Buy side is left alone: lifecycle_status tracks the listing, so it says
  // nothing meaningful about a buyer.
  const dimLate = !isBuySide
    && !["under_contract", "closed"].includes(seller.lifecycle_status || "active");

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <Link to="/admin/clients" className="btn-ghost" data-testid="back-to-sellers" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <ArrowLeft size={16} /> Back to clients
      </Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          {/* The eyebrow only earns its place when a client could be something
              else. With buy-side parked it always read "Seller", on a page that
              says so in every other way already. */}
          {(isBuySide || (Array.isArray(seller?.sides) && seller.sides.length > 1)) && (
            <span className="overline">{isBuySide ? "Buyer" : "Buyer + Seller"}</span>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 6 }}>
          <h1 className="font-serif" style={{ fontSize: "clamp(1.9rem, 3.6vw, 2.3rem)", fontWeight: 500, margin: 0, letterSpacing: "-0.01em" }} data-testid="seller-name">
            {seller.name}
          </h1>
            <DetailLifecyclePicker
              value={seller.lifecycle_status || "active"}
              onChange={setLifecycle}
            />
            {/* Client type sits beside the listing status because they are the same
                kind of fact about this client. It was a full-width card at the top of
                the page, which spent the best space on screen on a setting you pick
                once and never touch again. Only buyer-beta tenants can change it. */}
            {buyerFeaturesEnabled && (
              <>
                <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", marginLeft: 6 }}>Client type:</span>
                <div data-testid="client-type-editor" style={{ display: "inline-flex", gap: 4, background: "var(--surface-alt)", border: "1px solid var(--border)", borderRadius: 999, padding: 3 }}>
                  {[
                    { value: "sell", label: "Seller" },
                    { value: "buy",  label: "Buyer" },
                    { value: "both", label: "Both" },
                  ].map((opt) => {
                    const active = currentType === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`client-type-${opt.value}`}
                        disabled={typeBusy}
                        onClick={() => changeType(opt.value)}
                        style={{
                          border: "none", cursor: active || typeBusy ? "default" : "pointer",
                          padding: "7px 18px", borderRadius: 999,
                          fontSize: 12, fontWeight: 700, letterSpacing: "0.06em",
                          background: active ? "var(--primary)" : "transparent",
                          color: active ? "#fff" : "var(--text-muted)",
                          opacity: typeBusy && !active ? 0.5 : 1,
                          transition: "all 120ms ease",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <div style={{ color: "var(--text-muted)", marginBottom: 20 }}>
            {seller.email}{seller.property_address ? ` · ${seller.property_address}` : ""}
          </div>
        </div>
        <button
          data-testid="view-as-seller-btn"
          className="btn-secondary"
          onClick={viewAsSeller}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 16px", fontSize: 14 }}
        >
          <Eye size={14} /> View as seller
        </button>
      </div>

      {toast && <div data-testid="admin-toast" style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 10, background: "#E7F0EA", color: "var(--success)", fontSize: 14 }}>{toast}</div>}


      {/* Sell/Buy side switcher — only rendered when the client actually
          has both sides linked AND this tenant is in the buyer beta.
          Selecting a side reloads the RIGHT personalization doc and
          filters the tabs below to the ones relevant to that transaction. */}
      {buyerFeaturesEnabled && Array.isArray(seller?.sides) && seller.sides.length > 1 && (
        <div data-testid="admin-side-switch" style={{ display: "inline-flex", gap: 4, background: "var(--surface-alt)", border: "1px solid var(--border)", borderRadius: 999, padding: 3, marginBottom: 16 }}>
          {seller.sides.map((s) => {
            const active = (editSide || seller.active_side_for_edit) === s;
            return (
              <button
                key={s}
                type="button"
                data-testid={`admin-side-${s}`}
                onClick={() => {
                  setEditSide(s);
                  // Reset the tab to one that's valid for this side.
                  const validTab = TABS.find((t) => t.sides.includes(s));
                  if (validTab) setTab(validTab.id);
                }}
                style={{
                  border: "none", cursor: active ? "default" : "pointer",
                  padding: "6px 16px", borderRadius: 999,
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.10em",
                  background: active ? "var(--primary)" : "transparent",
                  color: active ? "#fff" : "var(--text-muted)",
                  transition: "all 120ms ease",
                }}
              >
                {s === "buy" ? "BUYING" : "SELLING"} SIDE
              </button>
            );
          })}
        </div>
      )}

      {/* Navigation and panel sit side by side: the rail on the left, the
          selected panel filling the rest. Tabs are still filtered to the
          side being edited; the rail only decides how they are grouped. */}
      <div className="seller-detail-split">
        <SellerRail
          tabs={TABS.filter((t) => t.sides.includes(isBuySide ? "buy" : "sell"))}
          groups={isBuySide ? RAIL_GROUPS.buy : RAIL_GROUPS.sell}
          active={tab}
          onPick={setTab}
          counts={railCounts}
          dimLate={dimLate}
        />
        <div className="seller-panel">

      {tab === "overview" && <OverviewTab p={p} seller={seller} submissions={submissions} isBuySide={isBuySide} />}
      {tab === "tasks" && <TasksTab p={p} save={save} />}
      {tab === "note" && <NoteTab p={p} save={save} />}
      {tab === "dates" && <DatesTab p={p} save={save} isBuySide={isBuySide} />}
      {tab === "under_contract" && <UnderContractTab p={p} save={save} />}
      {tab === "buyer_info" && <BuyerInfoTab p={p} save={save} />}
      {tab === "offers" && <OffersTab p={p} save={save} />}
      {tab === "showings" && <ShowingsTab key="sh-private" seller={seller} only="showing" />}
      {tab === "open_houses" && <ShowingsTab key="sh-openhouse" seller={seller} only="open_house" />}
      {tab === "feedback" && <FeedbackTab seller={seller} setToast={setToast} />}
      {tab === "activity" && <ActivityTab p={p} save={save} seller={seller} setToast={setToast} />}
      {tab === "social" && <SocialTab seller={seller} setToast={setToast} />}
      {tab === "listing_site" && <ListingSiteTab key={`site-${id}`} seller={seller} setToast={setToast} />}
      {tab === "print" && <PrintTab key={`print-${id}`} sellerId={seller?.id || seller?._id || id} setToast={setToast} />}
      {tab === "vendors" && <VendorsTab p={p} save={save} />}
      {tab === "wishlist" && <WishlistViewTab sellerId={id} />}
      {tab === "buyer_lender" && <BuyerLenderTab key={`lender-${id}`} p={p} save={save} />}
      {tab === "buyer_process" && <BuyerProcessTab key={`process-${id}`} p={p} save={save} />}
      {tab === "buyer_intake" && <BuyerIntakeTab key={`intake-${id}`} p={p} save={save} />}
      {tab === "buyer_closing" && <BuyerClosingTab key={`closing-${id}`} p={p} save={save} />}
      {tab === "net_sheet" && <NetSheetTab seller={seller} p={p} save={save} setToast={setToast} />}
      {tab === "listing_form" && <ListingFormTab seller={seller} setToast={setToast} />}
      {tab === "documents" && <DocumentsTab seller={seller} setToast={setToast} />}
      {tab === "submissions" && <SubmissionsTab items={submissions} setItems={setSubmissions} setToast={setToast} sellerId={seller.id} />}
      {tab === "client_forms" && <ClientFormsTab key={`cforms-${id}`} sellerId={seller?.id || seller?._id || id} setToast={setToast} />}
        </div>
      </div>
    </div>
  );
}

/* ---------- Tasks ---------- */
function TasksTab({ p, save }) {
  const [tasks, setTasks] = useState(p.custom_tasks || []);
  const [nt, setNt] = useState({ category: "repairs", custom_category_label: "", title: "", note: "", due_date: "" });

  const add = () => {
    if (!nt.title.trim()) return;
    setTasks([...tasks, { ...nt, id: `tmp_${Date.now()}` }]);
    setNt({ category: "repairs", custom_category_label: "", title: "", note: "", due_date: "" });
  };
  const remove = (idx) => setTasks(tasks.filter((_, i) => i !== idx));
  const saveAll = () => save({ custom_tasks: tasks }, "Tasks saved. Your seller will see them immediately.");

  return (
    <div>
      <div className="card" style={{ padding: 24, marginBottom: 16 }} data-testid="new-task-form">
        <div className="overline">Add a custom task</div>
        <div className="form-grid" style={{ marginTop: 12 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Category</label>
            <select data-testid="task-category" className="input-field" value={nt.category} onChange={(e) => setNt({ ...nt, category: e.target.value })}>
              {CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          {nt.category === "custom" && (
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Custom category name</label>
              <input data-testid="task-custom-cat" className="input-field" value={nt.custom_category_label} onChange={(e) => setNt({ ...nt, custom_category_label: e.target.value })} placeholder="e.g. Before Staging" />
            </div>
          )}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Task</label>
            <input data-testid="task-title" className="input-field" value={nt.title} onChange={(e) => setNt({ ...nt, title: e.target.value })} placeholder="e.g. Have the koi pond cleaned" />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Note <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span></label>
            <input data-testid="task-note" className="input-field" value={nt.note} onChange={(e) => setNt({ ...nt, note: e.target.value })} placeholder="Vendor: Greenline · before photo day" />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Due date <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span></label>
            <input data-testid="task-due" type="date" className="input-field" value={nt.due_date} onChange={(e) => setNt({ ...nt, due_date: e.target.value })} />
          </div>
        </div>
        <button data-testid="task-add-btn" type="button" className="btn-secondary" onClick={add} style={{ marginTop: 16 }}>
          <Plus size={14} /> Add task
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="task-list">
        {tasks.length === 0 ? (
          <div style={{ padding: 24, color: "var(--text-muted)" }}>No custom tasks yet. Add one above — it appears in this seller's Prep Guide right away.</div>
        ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr style={{ background: "var(--surface-alt)" }}>
                {["Category", "Task", "Note", "Due", ""].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "12px 16px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tasks.map((t, i) => {
                const catLabel = t.category === "custom" ? (t.custom_category_label || "Custom") : (CATS.find((c) => c.id === t.category)?.label || t.category);
                return (
                  <tr key={t.id || i} style={{ borderTop: "1px solid var(--border)" }} data-testid={`task-row-${i}`}>
                    <td style={{ padding: "12px 16px" }}><span className="badge">{catLabel}</span></td>
                    <td style={{ padding: "12px 16px", fontWeight: 500 }}>{t.title}</td>
                    <td style={{ padding: "12px 16px", color: "var(--text-muted)" }}>{t.note || "—"}</td>
                    <td style={{ padding: "12px 16px", color: "var(--text-muted)" }}>{t.due_date || "—"}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right" }}>
                      <button data-testid={`task-remove-${i}`} className="btn-ghost" onClick={() => remove(i)}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 20 }}>
        <button data-testid="tasks-save-btn" className="btn-primary" onClick={saveAll}>
          <Save size={14} /> Save changes
        </button>
      </div>

      {(p.seller_tasks || []).length > 0 && (
        <div className="card" style={{ padding: 24, marginTop: 32 }} data-testid="seller-added-tasks">
          <div className="overline">Tasks this seller added</div>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 12px" }}>
            These are self-added by your seller from their Prep Guide. Read-only here — they manage them on their end.
          </p>
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            <thead>
              <tr style={{ background: "var(--surface-alt)" }}>
                {["Category", "Task", "Note", "Added"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 14px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(p.seller_tasks || []).map((t) => {
                const catLabel = CATS.find((c) => c.id === t.category)?.label || t.category;
                return (
                  <tr key={t.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`seller-task-row-${t.id}`}>
                    <td style={{ padding: "10px 14px" }}><span className="badge">{catLabel}</span></td>
                    <td style={{ padding: "10px 14px", fontWeight: 500 }}>{t.title}</td>
                    <td style={{ padding: "10px 14px", color: "var(--text-muted)" }}>{t.note || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "var(--text-muted)", fontSize: 13 }}>
                      {t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <NotApplicablePanel p={p} save={save} />
    </div>
  );
}

/* ---------- Lifecycle picker (header pill) ---------- */
function DetailLifecyclePicker({ value, onChange }) {
  const cur = lifecycleLabel(value);
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <select
        data-testid="detail-lifecycle-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Set lifecycle status"
        style={{
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          background: cur.color,
          color: "#fff",
          border: "none",
          borderRadius: 999,
          padding: "5px 30px 5px 14px",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          cursor: "pointer",
          fontFamily: "inherit",
          lineHeight: 1.4,
        }}
      >
        {LIFECYCLE.map((l) => (
          <option key={l.id} value={l.id} style={{ color: "#000" }}>{l.label}</option>
        ))}
      </select>
      <ChevronDown
        size={13}
        color="#fff"
        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
      />
    </div>
  );
}


/* ---------- N/A applicability per seller ---------- */
function NotApplicablePanel({ p, save }) {
  const { content } = useContent();
  const [expanded, setExpanded] = useState(false);
  const hidden = new Set(p.hidden_prep_items || []);

  // Build the full set of items grouped by category — defaults from CMS + agent
  // custom_tasks added for this seller.
  const groups = PREP_CATEGORIES.map((cat) => {
    const customs = (p.custom_tasks || [])
      .filter((t) => t.category === cat.id)
      .map((t) => ({ id: t.id, label: t.title, fromAgent: true }));
    const defaults = (content.prep?.[cat.id] || []).map((it) => ({ id: it.id, label: it.label }));
    return { id: cat.id, label: cat.label, items: [...customs, ...defaults] };
  });

  // Custom-category items (no fixed section)
  const customGroups = {};
  (p.custom_tasks || []).forEach((t) => {
    if (t.category !== "custom") return;
    const label = t.custom_category_label || "Custom";
    customGroups[label] = customGroups[label] || [];
    customGroups[label].push({ id: t.id, label: t.title, fromAgent: true });
  });
  Object.entries(customGroups).forEach(([label, items]) => {
    groups.push({ id: `custom_${label}`, label, items });
  });

  const toggle = (itemId, currentlyHidden) => {
    const next = new Set(hidden);
    if (currentlyHidden) next.delete(itemId);
    else next.add(itemId);
    save({ hidden_prep_items: Array.from(next) }, currentlyHidden ? "Item restored." : "Marked as N/A.");
  };

  const hiddenCount = hidden.size;

  return (
    <div className="card" style={{ padding: 24, marginTop: 32 }} data-testid="na-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="overline">Mark prep items "Not Applicable"</div>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0" }}>
            Hide irrelevant prep tasks for this seller (e.g. no fireplace, no yard).
            They won't see them and the percent-done math adjusts automatically.
          </p>
          {hiddenCount > 0 && (
            <p data-testid="na-summary" style={{ color: "var(--secondary)", fontSize: 13, margin: "8px 0 0", fontWeight: 500 }}>
              {hiddenCount} item{hiddenCount === 1 ? "" : "s"} hidden for this seller.
            </p>
          )}
        </div>
        <button
          type="button"
          data-testid="na-toggle-btn"
          className="btn-secondary"
          onClick={() => setExpanded((v) => !v)}
          style={{ padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {expanded ? "Hide list" : "Manage items"}
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 18 }} data-testid="na-list">
          {groups.map((g) => (
            <div key={g.id}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{g.label}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {g.items.length === 0 ? (
                  <div style={{ color: "var(--text-faint)", fontSize: 13 }}>—</div>
                ) : (
                  g.items.map((it) => {
                    const isHidden = hidden.has(it.id);
                    return (
                      <label
                        key={it.id}
                        data-testid={`na-row-${it.id}`}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "8px 10px", borderRadius: 6,
                          background: isHidden ? "var(--surface-alt)" : "transparent",
                          fontSize: 14, cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          data-testid={`na-checkbox-${it.id}`}
                          checked={isHidden}
                          onChange={() => toggle(it.id, isHidden)}
                          style={{ accentColor: "var(--secondary)" }}
                        />
                        <span style={{
                          flex: 1,
                          color: isHidden ? "var(--text-muted)" : "var(--text)",
                          textDecoration: isHidden ? "line-through" : "none",
                        }}>{it.label}</span>
                        {it.fromAgent && <span className="badge" style={{ background: "#FBECE8", color: "var(--secondary)" }}>Custom</span>}
                        {isHidden && <span style={{ fontSize: 11, color: "var(--secondary)", fontWeight: 600 }}>N/A</span>}
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Note ---------- */
function NoteTab({ p, save }) {
  const [note, setNote] = useState(p.agent_note || "");
  return (
    <div className="card" style={{ padding: 28, maxWidth: 720 }}>
      <div className="overline">Message from you to this seller</div>
      <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4 }}>Appears on their dashboard. Keep it warm and specific.</p>
      <textarea data-testid="agent-note-input" className="input-field" rows={8} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Hi Jane! I'm excited to help you sell. Let's focus on…" style={{ marginTop: 14 }} />
      <button data-testid="note-save-btn" className="btn-primary" onClick={() => save({ agent_note: note }, "Note saved.")} style={{ marginTop: 14 }}>
        <Save size={14} /> Save note
      </button>
    </div>
  );
}

/* ---------- Dates ---------- */
function DatesTab({ p, save, isBuySide = false }) {
  const { content } = useContent();
  const [dates, setDates] = useState(p.key_dates || {});
  const [times, setTimes] = useState(p.key_dates_times || {});
  const [customFields, setCustomFields] = useState(p.custom_date_fields || []);
  const [hiddenFields, setHiddenFields] = useState(p.hidden_date_fields || []);
  const [newLabel, setNewLabel] = useState("");
  // On the buy side, timeline "stage_" dates don't apply the same way
  // (buyer stages aren't calendar events the same way sell stages are),
  // and the date field list swaps to buyer-flavored labels.
  const timelineStages = isBuySide
    ? []
    : ((content.timeline && content.timeline.length > 0) ? content.timeline : TIMELINE_STAGES);
  const dateFields = isBuySide
    ? BUYER_DATE_FIELDS
    : ((content.date_fields && content.date_fields.length > 0) ? content.date_fields : DATE_FIELDS);
  const visibleDateFields = dateFields.filter((f) => !hiddenFields.includes(f.key));
  const hiddenList = dateFields.filter((f) => hiddenFields.includes(f.key));

  const set = (key, value) => setDates((prev) => ({ ...prev, [key]: value }));
  const setTime = (key, value) => setTimes((prev) => {
    const next = { ...prev };
    if (value && value.trim()) next[key] = value;
    else delete next[key];
    return next;
  });

  const timePlaceholder = (key) => key === "open_house" ? "e.g. 11am – 1pm (optional)" : "e.g. 10:30 AM (optional)";

  const slugify = (label) => (label || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

  const hideField = (key) => {
    if (!window.confirm("Hide this date type for this seller? It won't appear on their dashboard or in their key dates editor. Any saved date for it will be cleared.")) return;
    setHiddenFields((xs) => xs.includes(key) ? xs : [...xs, key]);
    setDates((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setTimes((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const showField = (key) => {
    setHiddenFields((xs) => xs.filter((k) => k !== key));
  };

  const addCustom = () => {
    const label = newLabel.trim();
    if (!label) return;
    const usedKeys = new Set([
      ...dateFields.map((f) => f.key),
      ...customFields.map((f) => f.key),
      ...timelineStages.map((s) => `stage_${s.id}`),
    ]);
    let baseKey = slugify(label) || `custom_${Date.now().toString(36).slice(-4)}`;
    let key = `c_${baseKey}`;
    let n = 2;
    while (usedKeys.has(key)) { key = `c_${baseKey}_${n}`; n += 1; }
    setCustomFields((xs) => [...xs, { key, label }]);
    setNewLabel("");
  };

  const removeCustom = (key) => {
    if (!window.confirm("Remove this date type from this seller? Their saved date for it will also be cleared.")) return;
    setCustomFields((xs) => xs.filter((f) => f.key !== key));
    setDates((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setTimes((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const onSave = () => save(
    {
      key_dates: dates,
      key_dates_times: times,
      custom_date_fields: customFields,
      hidden_date_fields: hiddenFields,
    },
    "Dates saved.",
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="card" style={{ padding: 28, maxWidth: 720 }}>
        <div className="overline">Quick dates</div>
        <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4 }}>
          These appear on the seller's dashboard under "What's ahead." Manage the list of date types in <Link to="/admin/content" style={{ color: "var(--primary)", fontWeight: 500 }}>Admin → Content</Link>, or hide one just for this seller using the trash icon.
        </p>
        <div className="form-grid" style={{ marginTop: 16 }}>
          {visibleDateFields.map((f) => (
            <div key={f.key}>
              <label style={{ fontSize: 13, fontWeight: 500, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.label}</span>
                <button
                  type="button"
                  data-testid={`hide-date-${f.key}`}
                  onClick={() => hideField(f.key)}
                  className="btn-ghost"
                  style={{ padding: 4, color: "var(--secondary)" }}
                  title={`Hide "${f.label}" for this seller`}
                >
                  <Trash2 size={13} />
                </button>
              </label>
              <input data-testid={`date-${f.key}`} type="date" className="input-field" value={dates[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
              <input
                data-testid={`date-time-${f.key}`}
                type="text"
                className="input-field"
                value={times[f.key] || ""}
                onChange={(e) => setTime(f.key, e.target.value)}
                placeholder={timePlaceholder(f.key)}
                style={{ marginTop: 6, fontSize: 13 }}
              />
            </div>
          ))}
        </div>
        {hiddenList.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border)" }} data-testid="hidden-dates-row">
            <div className="overline" style={{ fontSize: 11, color: "var(--text-muted)" }}>Hidden for this seller</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {hiddenList.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`show-date-${f.key}`}
                  onClick={() => showField(f.key)}
                  className="btn-ghost"
                  style={{ padding: "4px 10px", fontSize: 12, border: "1px dashed var(--border)", borderRadius: 999, display: "inline-flex", alignItems: "center", gap: 6 }}
                  title={`Show "${f.label}" again for this seller`}
                >
                  <Plus size={11} /> {f.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 28, maxWidth: 720 }} data-testid="custom-dates-card">
        <div className="overline">Custom dates for this seller</div>
        <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4 }}>
          One-off dates that only apply to this seller (e.g. <i>HOA approval</i>, <i>Pool service</i>, <i>Buyer's deposit due</i>). Won't affect your other sellers.
        </p>

        {customFields.length > 0 && (
          <div className="form-grid" style={{ marginTop: 16 }}>
            {customFields.map((f) => (
              <div key={f.key} data-testid={`custom-date-${f.key}`}>
                <label style={{ fontSize: 13, fontWeight: 500, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 8 }}>
                  <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.label}</span>
                  <button
                    type="button"
                    data-testid={`custom-date-remove-${f.key}`}
                    onClick={() => removeCustom(f.key)}
                    className="btn-ghost"
                    style={{ padding: 4, color: "var(--secondary)" }}
                    title="Remove this custom date"
                  >
                    <Trash2 size={13} />
                  </button>
                </label>
                <input
                  data-testid={`custom-date-input-${f.key}`}
                  type="date"
                  className="input-field"
                  value={dates[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                />
                <input
                  data-testid={`custom-date-time-${f.key}`}
                  type="text"
                  className="input-field"
                  value={times[f.key] || ""}
                  onChange={(e) => setTime(f.key, e.target.value)}
                  placeholder="e.g. 2:00 PM (optional)"
                  style={{ marginTop: 6, fontSize: 13 }}
                />
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "stretch" }}>
          <input
            data-testid="custom-date-new-label"
            className="input-field"
            placeholder="Date label (e.g. HOA approval)"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            data-testid="custom-date-add"
            onClick={addCustom}
            disabled={!newLabel.trim()}
            className="btn-secondary"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
          >
            <Plus size={14} /> Add custom date
          </button>
        </div>
      </div>

      {timelineStages.length > 0 && (
        <div className="card" style={{ padding: 28, maxWidth: 720 }}>
          <div className="overline">Timeline stage dates</div>
          <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4 }}>
            Attach a date to any stage of the process. These show on the seller's Process page so they know when each step is happening.
          </p>
          <div className="form-grid" style={{ marginTop: 16 }}>
            {timelineStages.map((s, i) => {
              const key = `stage_${s.id}`;
              return (
                <div key={s.id}>
                  <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>
                    <span className="overline" style={{ marginRight: 6 }}>0{i + 1}</span>
                    {s.title}
                  </label>
                  <input
                    data-testid={`stage-date-${s.id}`}
                    type="date"
                    className="input-field"
                    value={dates[key] || ""}
                    onChange={(e) => set(key, e.target.value)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <button data-testid="dates-save-btn" className="btn-primary" onClick={onSave}>
          <Save size={14} /> Save dates
        </button>
      </div>
    </div>
  );
}

/* ---------- Under Contract ---------- */
function UnderContractTab({ p, save }) {
  const { content } = useContent();
  const cmsFields = content?.under_contract_fields || [];
  // Per-seller override resolution (Phase 2B.3.7-c). null = use agent's CMS.
  const initialFields = (p.under_contract_fields_override && p.under_contract_fields_override.length > 0)
    ? p.under_contract_fields_override
    : cmsFields;
  const initial = p.under_contract || {};
  const [active, setActive] = useState(!!initial.active);
  const [fields, setFields] = useState(() => initialFields.map((f) => ({ ...f })));
  const [items, setItems] = useState(() => {
    const out = {};
    for (const f of initialFields) {
      const v = initial[f.key] || {};
      out[f.key] = { date: v.date || "", time: v.time || "", description: v.description || "" };
    }
    return out;
  });
  const [editingFields, setEditingFields] = useState(false);

  const usingOverride = (p.under_contract_fields_override && p.under_contract_fields_override.length > 0);

  const setField = (key, sub, value) => setItems((prev) => ({
    ...prev,
    [key]: { ...(prev[key] || {}), [sub]: value },
  }));

  const slugify = (label) => (label || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || `uc_${Date.now().toString(36).slice(-4)}`;

  const updateField = (i, patch) => {
    const next = fields.map((f, j) => j === i ? { ...f, ...patch } : f);
    setFields(next);
    // If key changes, migrate items so saved values follow the rename.
    if (patch.key && patch.key !== fields[i].key) {
      setItems((prev) => {
        const v = prev[fields[i].key];
        if (!v) return prev;
        const { [fields[i].key]: _, ...rest } = prev;
        return { ...rest, [patch.key]: v };
      });
    }
  };
  const removeField = (i) => {
    if (!confirm(`Remove "${fields[i].label}"? Saved values for this milestone will stay in the database but won't appear on this seller's dashboard.`)) return;
    setFields(fields.filter((_, j) => j !== i));
  };
  const moveField = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    setFields(next);
  };
  const addField = () => {
    const used = new Set(fields.map((f) => f.key));
    let key = "new_milestone"; let n = 2;
    while (used.has(key)) { key = `new_milestone_${n}`; n += 1; }
    setFields([...fields, { key, label: "New Milestone", hasTime: false }]);
    setItems((prev) => ({ ...prev, [key]: { date: "", time: "", description: "" } }));
  };

  const onSave = () => {
    const payload = { active, ...items };
    // If the agent has customized fields away from the agent-wide defaults,
    // persist that override. If the current set exactly matches the agent's
    // CMS fields (same keys in same order), clear the override so this seller
    // keeps tracking future agent-wide changes.
    const matchesCms = fields.length === cmsFields.length &&
      fields.every((f, i) => f.key === cmsFields[i]?.key
                          && f.label === cmsFields[i]?.label
                          && !!f.hasTime === !!cmsFields[i]?.hasTime);
    save({
      under_contract: payload,
      under_contract_fields_override: matchesCms ? null : fields,
    }, active ? "Under-contract details saved." : "Saved. Toggle on whenever you're ready to share.");
  };

  const resetToDefaults = () => {
    if (!confirm("Reset this seller's milestones back to your default list? Custom milestones unique to this seller will be removed.")) return;
    setFields(cmsFields.map((f) => ({ ...f })));
    save({ under_contract_fields_override: [] }, "Reset to your default milestones.");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="card" style={{ padding: 24, maxWidth: 720 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div className="overline">Under contract</div>
            <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, maxWidth: 420 }}>
              Once your seller goes under contract, fill in these milestones and toggle the section on. They'll appear on the seller's dashboard as a dedicated card.
            </p>
            {usingOverride && (
              <p data-testid="uc-override-banner"
                 style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(157,65,51,0.08)",
                          color: "var(--secondary)", fontSize: 13, lineHeight: 1.5, display: "flex", gap: 8, alignItems: "center" }}>
                <FileSignature size={14} style={{ flexShrink: 0 }} />
                <span>Custom milestones for this seller — different from your defaults. <button onClick={resetToDefaults} data-testid="uc-reset-defaults" style={{ background: "none", border: "none", color: "var(--secondary)", textDecoration: "underline", cursor: "pointer", fontSize: 13, padding: 0 }}>Reset to defaults</button></span>
              </p>
            )}
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 10, fontSize: 14, fontWeight: 500, cursor: "pointer" }}>
            <input
              type="checkbox"
              data-testid="under-contract-active"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: "var(--primary)", cursor: "pointer" }}
            />
            Show on seller dashboard
          </label>
        </div>
      </div>

      <div className="card" style={{ padding: 20, maxWidth: 720, background: "var(--surface-alt)", border: "1px dashed var(--border)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div className="overline" style={{ margin: 0 }}>Milestones for this seller</div>
            <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "2px 0 0", lineHeight: 1.5 }}>
              {editingFields
                ? "Add, rename, reorder, or remove. Saving here only changes this seller — your defaults stay put."
                : "Edit this seller's milestones to fit their unique deal."}
            </p>
          </div>
          <button
            type="button"
            data-testid="uc-toggle-edit-fields"
            className="btn-ghost"
            onClick={() => setEditingFields((v) => !v)}
            style={{ padding: "6px 14px", fontSize: 13 }}
          >
            {editingFields ? "Done editing" : "Edit milestones"}
          </button>
        </div>

        {editingFields && (
          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            {fields.map((f, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 110px auto auto", gap: 8, alignItems: "center" }} data-testid={`uc-edit-row-${i}`}>
                <input
                  className="input-field"
                  data-testid={`uc-edit-label-${i}`}
                  value={f.label || ""}
                  onChange={(e) => {
                    const newLabel = e.target.value;
                    const auto = (fields[i].label === "New Milestone" || !fields[i].key.startsWith(slugify(fields[i].label).slice(0, 5)));
                    updateField(i, { label: newLabel, ...(auto ? { key: slugify(newLabel) } : {}) });
                  }}
                  placeholder="Label, e.g. Inspection"
                  style={{ fontSize: 14 }}
                />
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
                  <input
                    type="checkbox"
                    checked={!!f.hasTime}
                    onChange={(e) => updateField(i, { hasTime: e.target.checked })}
                    style={{ width: 14, height: 14, accentColor: "var(--primary)" }}
                    data-testid={`uc-edit-hastime-${i}`}
                  />
                  has time
                </label>
                <div style={{ display: "flex", gap: 2 }}>
                  <button type="button" onClick={() => moveField(i, -1)} disabled={i === 0} className="btn-ghost" style={{ padding: 6 }} data-testid={`uc-edit-up-${i}`}><ChevronUp size={14} /></button>
                  <button type="button" onClick={() => moveField(i, 1)} disabled={i === fields.length - 1} className="btn-ghost" style={{ padding: 6 }} data-testid={`uc-edit-down-${i}`}><ChevronDown size={14} /></button>
                </div>
                <button type="button" onClick={() => removeField(i)} className="btn-ghost" style={{ padding: 6, color: "var(--secondary)" }} data-testid={`uc-edit-remove-${i}`}><Trash2 size={14} /></button>
              </div>
            ))}
            <button type="button" onClick={addField} className="btn-secondary" data-testid="uc-edit-add"
                    style={{ marginTop: 4, alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <Plus size={14} /> Add milestone
            </button>
          </div>
        )}
      </div>

      {fields.map((f) => (
        <div key={f.key} className="card" style={{ padding: 24, maxWidth: 720 }} data-testid={`uc-section-${f.key}`}>
          <div className="overline">{f.label}</div>
          <div className="form-grid" style={{ marginTop: 12, gridTemplateColumns: f.hasTime ? "1fr 1fr" : "1fr" }}>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Date</label>
              <input
                data-testid={`uc-date-${f.key}`}
                type="date"
                className="input-field"
                value={(items[f.key] || {}).date || ""}
                onChange={(e) => setField(f.key, "date", e.target.value)}
              />
            </div>
            {f.hasTime && (
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Time</label>
                <input
                  data-testid={`uc-time-${f.key}`}
                  type="time"
                  className="input-field"
                  value={(items[f.key] || {}).time || ""}
                  onChange={(e) => setField(f.key, "time", e.target.value)}
                />
              </div>
            )}
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Description (optional)</label>
            <textarea
              data-testid={`uc-desc-${f.key}`}
              className="input-field"
              rows={2}
              value={(items[f.key] || {}).description || ""}
              onChange={(e) => setField(f.key, "description", e.target.value)}
              placeholder="What should your seller know about this milestone?"
            />
          </div>
        </div>
      ))}

      <div>
        <button data-testid="under-contract-save-btn" className="btn-primary" onClick={onSave}>
          <Save size={14} /> Save under contract details
        </button>
      </div>
    </div>
  );
}

/* ---------- Buyer Welcome Info per-seller override ----------
   Lets the agent customize the Buyer Welcome Info form for THIS seller —
   e.g. drop the HOA field for a single-family home, or add a "pool
   maintenance" field for one with a pool. Mirrors the under-contract field
   editor: per-seller override wins if set, otherwise the agent's CMS defaults
   (Admin → Content → Forms → Buyer Welcome Info form) are used. The seller's
   already-saved answers are not deleted by removing a field — they just stop
   showing on the form (and won't appear in future PDFs). */
function BuyerInfoTab({ p, save }) {
  const { content } = useContent();
  const cmsFields = content?.buyer_info_fields || [];
  const initialFields = (p.buyer_info_fields_override && p.buyer_info_fields_override.length > 0)
    ? p.buyer_info_fields_override
    : cmsFields;

  const [fields, setFields] = useState(() => initialFields.map((f) => ({ ...f })));
  const usingOverride = (p.buyer_info_fields_override && p.buyer_info_fields_override.length > 0);

  const slugify = (label) => (label || "")
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || `bi_${Date.now().toString(36).slice(-4)}`;

  const updateField = (i, patch) => setFields(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const removeField = (i) => {
    if (!confirm(`Remove "${fields[i].label}" from this seller's form? Their existing answer (if any) stays in the database but won't appear in the form or PDF.`)) return;
    setFields(fields.filter((_, j) => j !== i));
  };
  const moveField = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    setFields(next);
  };
  const addField = (kind) => {
    const used = new Set(fields.map((f) => f.key));
    let baseKey = kind === "long" ? "new_note" : "new_field";
    let key = baseKey;
    let n = 2;
    while (used.has(key)) { key = `${baseKey}_${n}`; n += 1; }
    setFields([...fields, { key, kind, label: kind === "long" ? "New note" : "New field", placeholder: "", hint: "" }]);
  };

  const onSave = () => {
    const matchesCms = fields.length === cmsFields.length &&
      fields.every((f, i) =>
        f.key === cmsFields[i]?.key &&
        f.label === cmsFields[i]?.label &&
        (f.kind || "short") === (cmsFields[i]?.kind || "short")
      );
    save(
      { buyer_info_fields_override: matchesCms ? null : fields },
      matchesCms ? "Reset to your default Buyer Welcome form." : "Custom Buyer Welcome form saved for this seller."
    );
  };
  const resetToDefaults = () => {
    if (!confirm("Reset this seller's Buyer Welcome form to your default list? Custom fields unique to this seller will be removed.")) return;
    setFields(cmsFields.map((f) => ({ ...f })));
  };

  return (
    <div className="card" style={{ padding: 28 }} data-testid="buyer-info-override-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
        <div>
          <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Buyer Welcome Info form</h2>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0", maxWidth: 640 }}>
            Customize the under-contract form for <b>this seller only</b>. Add, rename, reorder, or remove fields without touching your default form. {usingOverride
              ? <span style={{ color: "var(--secondary)", fontWeight: 500 }}>· Currently using a per-seller override</span>
              : <span>· Currently using your default form</span>}
          </p>
        </div>
        {usingOverride && (
          <button type="button" className="btn-ghost" onClick={resetToDefaults} data-testid="bi-override-reset" style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Reset to default
          </button>
        )}
      </div>

      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        {fields.map((f, i) => (
          <div key={i} data-testid={`bi-override-row-${i}`} style={{
            background: "var(--surface-alt)", padding: 14, borderRadius: 10, border: "1px solid var(--border)",
          }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr) 110px auto", gap: 10, alignItems: "end" }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Display label</label>
                <input data-testid={`bi-override-label-${i}`} className="input-field" value={f.label || ""} onChange={(e) => {
                  const newLabel = e.target.value;
                  const auto = (f.label === "New field" || f.label === "New note" || !f.key);
                  updateField(i, { label: newLabel, ...(auto ? { key: slugify(newLabel) } : {}) });
                }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Internal key</label>
                <input data-testid={`bi-override-key-${i}`} className="input-field" value={f.key || ""} onChange={(e) => updateField(i, { key: slugify(e.target.value) })} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Kind</label>
                <select data-testid={`bi-override-kind-${i}`} className="input-field" value={f.kind || "short"} onChange={(e) => updateField(i, { kind: e.target.value })}>
                  <option value="short">Short</option>
                  <option value="long">Long</option>
                </select>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button type="button" className="btn-ghost" onClick={() => moveField(i, -1)} disabled={i === 0} style={{ padding: "8px 10px" }}>↑</button>
                <button type="button" className="btn-ghost" onClick={() => moveField(i, +1)} disabled={i === fields.length - 1} style={{ padding: "8px 10px" }}>↓</button>
                <button type="button" className="btn-ghost" onClick={() => removeField(i)} style={{ padding: "8px 10px", color: "var(--secondary)" }}>×</button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
              <input data-testid={`bi-override-placeholder-${i}`} className="input-field" value={f.placeholder || ""} onChange={(e) => updateField(i, { placeholder: e.target.value })} placeholder="Placeholder shown inside the empty field" />
              <input data-testid={`bi-override-hint-${i}`} className="input-field" value={f.hint || ""} onChange={(e) => updateField(i, { hint: e.target.value })} placeholder="(Optional) hint shown above the field" />
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <button type="button" className="btn-ghost" onClick={() => addField("short")} data-testid="bi-override-add-short" style={{ fontSize: 13 }}>+ Add short field</button>
        <button type="button" className="btn-ghost" onClick={() => addField("long")} data-testid="bi-override-add-long" style={{ fontSize: 13 }}>+ Add long-form note</button>
      </div>

      <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end", paddingTop: 18, borderTop: "1px solid var(--border)" }}>
        <button type="button" className="btn-primary" onClick={onSave} data-testid="bi-override-save">Save</button>
      </div>
    </div>
  );
}

/* ---------- Vendors ---------- */
/* ---------- Offers ---------- */
const BLANK_OFFER = {
  name: "",
  purchase_price: "",
  concessions: "0",
  buyer_commission_pct: 3,
  closing_date: "",
  note: "",
};

function OffersTab({ p, save }) {
  const [offers, setOffers] = useState(() => {
    const initial = (p.offers || []).map((o) => ({ ...BLANK_OFFER, ...o }));
    if (initial.length === 0) {
      return [{ ...BLANK_OFFER, name: "Offer A" }];
    }
    return initial;
  });

  const setOffer = (idx, patch) => setOffers((xs) => xs.map((o, i) => i === idx ? { ...o, ...patch } : o));
  const addOffer = () => {
    if (offers.length >= 3) return;
    const letters = ["A", "B", "C"];
    setOffers((xs) => [...xs, { ...BLANK_OFFER, name: `Offer ${letters[xs.length] || xs.length + 1}` }]);
  };
  const removeOffer = (idx) => {
    if (!window.confirm("Remove this offer?")) return;
    setOffers((xs) => xs.filter((_, i) => i !== idx));
  };

  const onSave = () => save({ offers }, "Offers saved — your seller can see them now in their Net Sheet.");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="card" style={{ padding: 24, maxWidth: 720 }}>
        <div className="overline">Offers received</div>
        <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 6 }}>
          Enter incoming offers here. They'll appear pre-filled on your seller's <b>Net Sheet → Compare offers</b> tab. Sellers can still edit values themselves; new offers you add will sync up automatically.
        </p>
      </div>

      {offers.length === 0 && (
        <div className="card" style={{ padding: 24, color: "var(--text-muted)", maxWidth: 720 }}>
          No offers yet. Click <b>+ Add offer</b> below to enter the first one.
        </div>
      )}

      {offers.map((o, idx) => (
        <div key={idx} className="card" style={{ padding: 24, maxWidth: 720 }} data-testid={`admin-offer-${idx}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div className="overline">Offer {String.fromCharCode(65 + idx)}</div>
            <button
              type="button"
              onClick={() => removeOffer(idx)}
              className="btn-ghost"
              style={{ padding: 6, color: "var(--secondary)" }}
              data-testid={`admin-offer-remove-${idx}`}
              title="Remove offer"
            >
              <Trash2 size={14} />
            </button>
          </div>
          <div className="form-grid">
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Offer name / buyer</label>
              <input data-testid={`admin-offer-name-${idx}`} className="input-field" value={o.name} onChange={(e) => setOffer(idx, { name: e.target.value })} placeholder="e.g. Smith Family · cash · 20% down" />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Purchase Price</label>
              <input data-testid={`admin-offer-price-${idx}`} className="input-field" type="number" value={o.purchase_price} onChange={(e) => setOffer(idx, { purchase_price: e.target.value })} placeholder="500000" />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Seller Concessions</label>
              <input data-testid={`admin-offer-concessions-${idx}`} className="input-field" type="number" value={o.concessions} onChange={(e) => setOffer(idx, { concessions: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Buyer Commission (%)</label>
              <input data-testid={`admin-offer-buyer-pct-${idx}`} className="input-field" type="number" step="0.1" value={o.buyer_commission_pct} onChange={(e) => setOffer(idx, { buyer_commission_pct: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Proposed Closing Date</label>
              <input data-testid={`admin-offer-closing-${idx}`} className="input-field" type="date" value={o.closing_date} onChange={(e) => setOffer(idx, { closing_date: e.target.value })} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Notes (optional)</label>
              <input data-testid={`admin-offer-note-${idx}`} className="input-field" value={o.note} onChange={(e) => setOffer(idx, { note: e.target.value })} placeholder="Anything else worth flagging — financing terms, contingencies, etc." />
            </div>
          </div>
        </div>
      ))}

      <div style={{ display: "flex", gap: 8 }}>
        {offers.length < 3 && (
          <button type="button" onClick={addOffer} className="btn-secondary" data-testid="admin-offer-add">
            <Plus size={14} /> Add offer
          </button>
        )}
        <button type="button" onClick={onSave} className="btn-primary" data-testid="admin-offer-save">
          <Save size={14} /> Save offers
        </button>
      </div>
    </div>
  );
}


/* ---------- Showings ---------- */
function ToggleSwitch({ on, onClick, testid, titleOn, titleOff }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      title={on
        ? (titleOn || "Showing on seller's page — click to hide")
        : (titleOff || "Hidden from seller — click to show")}
      style={{
        border: "none", cursor: "pointer", flexShrink: 0,
        width: 36, height: 20, borderRadius: 999, padding: 2,
        background: on ? "var(--primary)" : "var(--border)",
        display: "inline-flex", alignItems: "center",
        justifyContent: on ? "flex-end" : "flex-start",
        transition: "all 120ms ease",
      }}
    >
      <span style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", display: "block" }} />
    </button>
  );
}

const SOURCE_GRID = "36px minmax(0,1fr) 76px 92px 76px 32px";

/* ---------- Seller update emails ---------- */

const UPDATE_KINDS = [
  { key: "feedback", label: "New buyer feedback", hint: "Only the notes you've marked as shared." },
  { key: "showing", label: "Showings & open houses", hint: "Added, rescheduled, cancelled, or an open house recap." },
  { key: "activity", label: "Listing numbers", hint: "Views, saves, and the sources you're tracking." },
  { key: "listing", label: "Listing goes live", hint: "One note the day you flip this listing to Active." },
];

/** Per-seller control over the update digest. Changes made on the agent side
 *  queue up and go out as one email once the agent stops editing — this card
 *  shows what's waiting and lets her send it early or turn a category off. */
function SellerUpdateEmails({ seller, setToast, refreshKey }) {
  const sellerId = seller?.id || seller?._id;
  const [prefs, setPrefs] = useState(null);
  const [pending, setPending] = useState([]);
  const [quietMinutes, setQuietMinutes] = useState(45);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!sellerId) return;
    try {
      const { data } = await api.get(`/admin/sellers/${sellerId}/update-emails`);
      setPrefs(data.prefs || {});
      setPending(data.pending || []);
      if (data.quiet_minutes) setQuietMinutes(data.quiet_minutes);
    } catch {
      setPrefs({});
    }
  };
  // `refreshKey` bumps after the agent saves activity numbers, so the
  // "waiting to send" list reflects the change she just made.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sellerId, refreshKey]);

  const toggle = async (key) => {
    const prior = prefs;
    const next = { ...prefs, [key]: prefs?.[key] === false };
    setPrefs(next);                       // optimistic — the switch shouldn't lag
    try {
      const { data } = await api.put(`/admin/sellers/${sellerId}/update-emails`, { [key]: next[key] });
      setPrefs(data.prefs || next);
      if (key === "enabled" && next[key] === false) setPending([]);
    } catch (err) {
      setPrefs(prior);                    // roll back so the switch never lies
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't save that");
    }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/sellers/${sellerId}/update-emails/send-now`);
      setPending([]);
      setToast(`Sent — ${data.sent} update${data.sent === 1 ? "" : "s"} on their way.`);
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't send that email");
    } finally { setBusy(false); }
  };

  if (!prefs) return null;
  const on = prefs.enabled !== false;

  return (
    <div className="card" style={{ padding: 24, maxWidth: 720 }} data-testid="update-emails-card">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <ToggleSwitch
          on={on} onClick={() => toggle("enabled")} testid="update-emails-toggle"
          titleOn="Emailing this seller — click to stop" titleOff="Not emailing this seller — click to start"
        />
        <div style={{ flex: 1 }}>
          <div className="overline" style={{ marginTop: 2 }}>Email updates to your seller</div>
          <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, marginBottom: 0, maxWidth: 560 }}>
            When you update things here, your seller gets one email rounding it all up — sent about{" "}
            {quietMinutes} minutes after you stop making changes, so an afternoon of edits is a single
            email rather than a dozen. They'll never get more than one every few hours.
          </p>
        </div>
      </div>

      {on && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 18 }}>
            {UPDATE_KINDS.map((k) => {
              const enabled = prefs[k.key] !== false;
              return (
                <div
                  key={k.key}
                  style={{
                    display: "grid", gridTemplateColumns: "36px 1fr", gap: 14,
                    alignItems: "center", padding: "10px 0",
                    borderBottom: "1px solid var(--border)",
                    opacity: enabled ? 1 : 0.5,
                  }}
                >
                  <ToggleSwitch
                    on={enabled} onClick={() => toggle(k.key)} testid={`update-emails-${k.key}-toggle`}
                    titleOn="Included in update emails — click to leave it out"
                    titleOff="Left out of update emails — click to include it"
                  />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{k.label}</div>
                    <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{k.hint}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 18 }} data-testid="update-emails-pending">
            {pending.length === 0 ? (
              <div style={{ fontSize: 13.5, color: "var(--text-muted)" }}>
                Nothing waiting to send right now.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13.5, fontWeight: 500, marginBottom: 8 }}>
                  Waiting to send ({pending.length}):
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, color: "var(--text-muted)", fontSize: 13.5, lineHeight: 1.7 }}>
                  {pending.map((e, i) => (
                    <li key={i}>
                      {e.context ? <span style={{ opacity: 0.75 }}>{e.context} — </span> : null}
                      {e.line}
                    </li>
                  ))}
                </ul>
                <button
                  type="button" className="btn-secondary" onClick={sendNow} disabled={busy}
                  style={{ marginTop: 14 }} data-testid="update-emails-send-now"
                >
                  <Send size={14} /> {busy ? "Sending…" : "Send now"}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ActivityTab({ p, save, seller, setToast }) {
  const [stats, setStats] = useState(() => normalizeActivityStats(p.activity_stats));
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  const setField = (key, value) =>
    setStats((s) => ({ ...s, [key]: { ...s[key], value: Math.max(0, parseInt(value, 10) || 0) } }));
  const toggleField = (key) =>
    setStats((s) => ({ ...s, [key]: { ...s[key], enabled: !s[key]?.enabled } }));
  // Switching a stat back to auto doesn't guess a number — the backend
  // recounts from the calendar on the next showing change, and the save
  // response brings the real figure back.
  const toggleAuto = (key) =>
    setStats((s) => ({ ...s, [key]: { ...s[key], auto: s[key]?.auto === false } }));

  const patchSource = (idx, patch) =>
    setStats((s) => ({
      ...s,
      platforms: s.platforms.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    }));
  const addSource = () =>
    setStats((s) => ({ ...s, platforms: [...s.platforms, newPlatformRow(`${Date.now()}${s.platforms.length}`)] }));
  const removeSource = (idx) =>
    setStats((s) => ({ ...s, platforms: s.platforms.filter((_, i) => i !== idx) }));

  const onSave = async () => {
    setSaving(true);
    try {
      // Drop half-finished unnamed rows so they never reach the seller.
      const cleaned = { ...stats, platforms: stats.platforms.filter((r) => r.label.trim()) };
      setStats(cleaned);
      await save({ activity_stats: cleaned }, "Activity updated. Your seller will see the new numbers immediately.");
      setSavedCount((n) => n + 1);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="card" style={{ padding: 24, maxWidth: 720 }}>
        <div className="overline">Home activity</div>
        <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, maxWidth: 560 }}>
          Showings count themselves from your calendar, and open house groups add up from the counts you
          record on each open house. The rest you enter by hand. Flip a switch off to hide that stat from
          your seller's <b>Home Activity</b> page entirely.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 16 }}>
          {ACTIVITY_STAT_FIELDS.map((f) => {
            const field = stats[f.key] || { value: 0, enabled: true };
            const auto = isAutoStat(f.key) && field.auto !== false;
            return (
              <div
                key={f.key}
                style={{
                  display: "grid",
                  gridTemplateColumns: "36px 1fr 140px",
                  gap: 14,
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: "1px solid var(--border)",
                  opacity: field.enabled ? 1 : 0.5,
                }}
              >
                <ToggleSwitch on={field.enabled} onClick={() => toggleField(f.key)} testid={`activity-${f.key}-toggle`} />
                <div>
                  <label style={{ fontSize: 14, fontWeight: 500, display: "block" }}>{f.label}</label>
            {isAutoStat(f.key) && isLockedStat(f.key) && (
              <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>
                {f.auto}
              </div>
            )}
            {isAutoStat(f.key) && !isLockedStat(f.key) && (
                    <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>
                      {auto ? f.auto : "You're entering this one yourself"}
                      {" · "}
                      <button
                        type="button"
                        className="btn-ghost"
                        data-testid={`activity-${f.key}-auto`}
                        onClick={() => toggleAuto(f.key)}
                        style={{ padding: 0, fontSize: 12.5, fontWeight: 600, color: "var(--primary)" }}
                      >
                        {auto ? "enter it myself" : (f.autoVerb || "count it for me")}
                      </button>
                    </div>
                  )}
                </div>
                <input
                  data-testid={`activity-${f.key}`}
                  type="number"
                  min="0"
                  className="input-field"
                  // An auto stat is a readout, not a field — editing it would be
                  // overwritten the next time the calendar changes.
                  disabled={!field.enabled || auto}
                  title={auto ? "Counted automatically — switch to manual to edit" : undefined}
                  value={field.value}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="card" style={{ padding: 24, maxWidth: 720 }}>
        <div className="overline">Where it's being seen</div>
        <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, marginBottom: 16 }}>
          Every place the listing is posted. Add as many sources as you like, rename them, or switch one off
          if it isn't live there yet. <b>Label</b> is the second number's name — Saves, Leads, Inquiries, whatever fits.
        </p>

        <div
          style={{
            display: "grid", gridTemplateColumns: SOURCE_GRID, gap: 8,
            fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase",
            color: "var(--text-muted)", fontWeight: 600, paddingBottom: 6,
          }}
        >
          <span />
          <span>Source</span>
          <span>Views</span>
          <span>Label</span>
          <span>Count</span>
          <span />
        </div>

        <div style={{ display: "flex", flexDirection: "column" }} data-testid="activity-source-list">
          {stats.platforms.length === 0 && (
            <div style={{ color: "var(--text-muted)", fontSize: 14, padding: "12px 0" }}>
              No sources yet. Add the first one below.
            </div>
          )}
          {stats.platforms.map((row, idx) => (
            <div
              key={row.id}
              data-testid={`activity-source-${idx}`}
              style={{
                display: "grid", gridTemplateColumns: SOURCE_GRID, gap: 8,
                alignItems: "center", padding: "8px 0",
                borderBottom: "1px solid var(--border)",
                opacity: row.enabled ? 1 : 0.5,
              }}
            >
              <ToggleSwitch on={row.enabled} onClick={() => patchSource(idx, { enabled: !row.enabled })} testid={`activity-source-${idx}-toggle`} />
              <input
                data-testid={`activity-source-${idx}-label`}
                className="input-field"
                placeholder="e.g. Homes.com"
                disabled={!row.enabled}
                value={row.label}
                onChange={(e) => patchSource(idx, { label: e.target.value })}
              />
              <input
                data-testid={`activity-source-${idx}-views`}
                type="number" min="0" className="input-field"
                disabled={!row.enabled}
                value={row.views}
                onChange={(e) => patchSource(idx, { views: Math.max(0, parseInt(e.target.value, 10) || 0) })}
              />
              <input
                data-testid={`activity-source-${idx}-metric-label`}
                className="input-field"
                placeholder="Saves"
                disabled={!row.enabled}
                value={row.metric_label}
                onChange={(e) => patchSource(idx, { metric_label: e.target.value })}
              />
              <input
                data-testid={`activity-source-${idx}-metric-value`}
                type="number" min="0" className="input-field"
                disabled={!row.enabled}
                value={row.metric_value}
                onChange={(e) => patchSource(idx, { metric_value: Math.max(0, parseInt(e.target.value, 10) || 0) })}
              />
              <button
                type="button"
                className="btn-ghost"
                data-testid={`activity-source-${idx}-remove`}
                title="Remove this source"
                onClick={() => removeSource(idx)}
                style={{ padding: 4 }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <button type="button" className="btn-secondary" onClick={addSource} style={{ marginTop: 16 }} data-testid="activity-add-source">
          <Plus size={14} /> Add source
        </button>
      </div>

      <div>
        <button data-testid="activity-save-btn" className="btn-primary" onClick={onSave} disabled={saving}>
          <Save size={14} /> {saving ? "Saving…" : "Save activity"}
        </button>
      </div>

      <SellerUpdateEmails seller={seller} setToast={setToast} refreshKey={savedCount} />
    </div>
  );
}

/* ---------- Social Posts ---------- */

const SOCIAL_PLATFORMS = [
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
  { id: "tiktok", label: "TikTok" },
];

const SOCIAL_POST_TYPES = [
  { id: "post", label: "Feed post" },
  { id: "reel", label: "Reel" },
  { id: "story", label: "Story" },
  { id: "carousel", label: "Carousel" },
  { id: "video", label: "Video" },
];

const SOCIAL_TONES = [
  { id: "warm", label: "Warm & human" },
  { id: "polished", label: "Polished & editorial" },
  { id: "punchy", label: "Punchy & scroll-stopping" },
  { id: "informative", label: "Straightforward & factual" },
];

/* Thumbnails need auth, and <img src> can't send our Bearer header, so the
   bytes come through the API client and the <img> gets an object URL.

   One fetch per photo per session, shared by every tile that shows it. The
   same photo appears in the pool, in each post's picker and on the attached
   strip, and each of those used to fetch its own copy — the same megabyte
   downloaded two or three times every visit to this tab. The cache also means
   the photos are simply *there* when you come back to the tab, instead of
   flashing grey while they reload. */
const photoUrls = new Map();      // photo_id -> object URL, already loaded
const photoInflight = new Map();  // photo_id -> Promise, in flight right now

function fetchPhoto(photoId) {
  const done = photoUrls.get(photoId);
  if (done) return Promise.resolve(done);
  const pending = photoInflight.get(photoId);
  if (pending) return pending;

  const p = api
    .get(`/listing-photos/${photoId}/file`, { responseType: "blob" })
    .then(({ data }) => {
      const url = URL.createObjectURL(data);
      photoUrls.set(photoId, url);
      photoInflight.delete(photoId);
      return url;
    })
    .catch((err) => {
      // Drop it so a retry — or the next tile — can try again. Caching the
      // failure is what turned one dropped request into a tile that stayed
      // blank until you left the page.
      photoInflight.delete(photoId);
      throw err;
    });
  photoInflight.set(photoId, p);
  return p;
}

/** Called when a photo is deleted — the cache owns the object URL, so it's the
 *  only thing allowed to release it. */
function forgetPhoto(photoId) {
  const url = photoUrls.get(photoId);
  if (url) URL.revokeObjectURL(url);
  photoUrls.delete(photoId);
  photoInflight.delete(photoId);
}

function AuthImage({ photoId, alt, style }) {
  const [src, setSrc] = useState(() => photoUrls.get(photoId) || "");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const cached = photoUrls.get(photoId);
    if (cached) { setSrc(cached); setFailed(false); return undefined; }

    let alive = true;
    let timer;
    setSrc("");
    setFailed(false);

    // One silent retry. These are half-megabyte images fetched several at a
    // time; an occasional one gets dropped, and before this that meant a
    // permanently grey square with the error swallowed.
    const attempt = (retries) =>
      fetchPhoto(photoId)
        .then((url) => { if (alive) setSrc(url); })
        .catch(() => {
          if (!alive) return;
          if (retries > 0) timer = setTimeout(() => attempt(retries - 1), 700);
          else setFailed(true);
        });
    attempt(1);

    // Deliberately no revoke here: the URL belongs to the cache and other
    // tiles are still using it.
    return () => { alive = false; clearTimeout(timer); };
  }, [photoId]);

  if (src) return <img src={src} alt={alt || ""} style={style} />;
  // Not a button — this renders inside one on the post picker.
  return (
    <div
      title={failed ? "This photo didn't load. Reload the page to try again." : ""}
      style={{
        ...style, background: "var(--surface-alt)", display: "flex",
        alignItems: "center", justifyContent: "center",
        color: "var(--text-muted)", fontSize: 18,
      }}
    >
      {failed ? "!" : ""}
    </div>
  );
}

function SocialTab({ seller, setToast }) {
  const sellerId = seller?.id || seller?._id;
  const [photos, setPhotos] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ platform: "instagram", post_type: "post", tone: "warm", emphasis: "" });
  const fileRef = useRef(null);

  const load = async () => {
    if (!sellerId) return;
    try {
      const [ph, po] = await Promise.all([
        api.get(`/admin/seller/${sellerId}/listing-photos`),
        api.get(`/admin/seller/${sellerId}/social-posts`),
      ]);
      setPhotos(ph.data.photos || []);
      setPosts(po.data.posts || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sellerId]);

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/admin/seller/${sellerId}/listing-photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setPhotos((xs) => [...xs, ...(data.photos || [])]);
      const skipped = data.skipped || [];
      setToast(
        skipped.length
          ? `Added ${data.photos.length} photo${data.photos.length === 1 ? "" : "s"}. Skipped ${skipped.length}: ${skipped[0].reason}.`
          : `Added ${data.photos.length} photo${data.photos.length === 1 ? "" : "s"}.`
      );
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const removePhoto = async (photoId) => {
    if (!window.confirm("Remove this photo? It will also come off any post using it.")) return;
    try {
      await api.delete(`/admin/listing-photos/${photoId}`);
      forgetPhoto(photoId);
      setPhotos((xs) => xs.filter((p) => p.photo_id !== photoId));
      setPosts((xs) => xs.map((p) => ({ ...p, photo_ids: (p.photo_ids || []).filter((id) => id !== photoId) })));
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't remove that photo");
    }
  };

  const generate = async (postId) => {
    setGenerating(true);
    try {
      const { data } = await api.post(
        `/admin/seller/${sellerId}/social-posts/generate`,
        { ...form, post_id: postId || null },
        { timeout: 120000 },
      );
      setPosts((xs) => postId ? xs.map((p) => (p.post_id === postId ? data.post : p)) : [data.post, ...xs]);
      setToast(postId ? "Rewritten." : "Caption written. Edit it however you like before posting.");
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't write a caption");
    } finally { setGenerating(false); }
  };

  const patchPost = async (postId, patch) => {
    try {
      const { data } = await api.put(`/admin/social-posts/${postId}`, patch);
      setPosts((xs) => xs.map((p) => (p.post_id === postId ? data.post : p)));
      return data.post;
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't save that change");
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Delete this post draft?")) return;
    await api.delete(`/admin/social-posts/${postId}`);
    setPosts((xs) => xs.filter((p) => p.post_id !== postId));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* ----- Photo library ----- */}
      <div className="card" style={{ padding: 24, maxWidth: 860 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div className="overline">Listing photos</div>
            <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, maxWidth: 520 }}>
              Upload once for this listing — every post below picks from this pool. JPG, PNG or WebP, up to 12 MB each.
            </p>
          </div>
          <button type="button" className="btn-secondary" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="social-upload-btn">
            <ImagePlus size={14} /> {uploading ? "Uploading…" : "Add photos"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickFiles} style={{ display: "none" }} data-testid="social-file-input" />
        </div>

        {loading ? (
          <div style={{ color: "var(--text-muted)", marginTop: 16 }}>Loading…</div>
        ) : photos.length === 0 ? (
          <div style={{ color: "var(--text-muted)", marginTop: 16, fontSize: 14 }} data-testid="social-photos-empty">
            No photos yet. Add a few and they'll be ready to attach to any post.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(112px, 1fr))", gap: 10, marginTop: 16 }} data-testid="social-photo-grid">
            {photos.map((ph) => (
              <div key={ph.photo_id} style={{ position: "relative", borderRadius: 10, overflow: "hidden", border: "1px solid var(--border)" }}>
                <AuthImage photoId={ph.photo_id} alt={ph.filename} style={{ width: "100%", height: 84, objectFit: "cover", display: "block" }} />
                <button
                  type="button"
                  onClick={() => removePhoto(ph.photo_id)}
                  title="Remove photo"
                  data-testid={`social-photo-remove-${ph.photo_id}`}
                  style={{
                    position: "absolute", top: 4, right: 4, border: "none", cursor: "pointer",
                    background: "rgba(0,0,0,0.55)", color: "#fff", borderRadius: "50%",
                    width: 20, height: 20, lineHeight: "18px", fontSize: 13, padding: 0,
                  }}
                >×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ----- Campaign builder (photos + headlines composed into finished graphics) ----- */}
      <CampaignBuilder sellerId={sellerId} photos={photos} setToast={setToast} />

      {/* ----- Single caption generator (text only, no graphic) ----- */}
      <div className="card" style={{ padding: 24, maxWidth: 860 }}>
        <div className="overline">Just need a caption?</div>
        <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, marginBottom: 16, maxWidth: 560 }}>
          Written from the listing details already on file for this seller. Nothing posts automatically — you'll
          copy the caption into the app yourself.
        </p>
        <div className="form-grid">
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Platform</label>
            <select className="input-field" data-testid="social-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
              {SOCIAL_PLATFORMS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Format</label>
            <select className="input-field" data-testid="social-post-type" value={form.post_type} onChange={(e) => setForm({ ...form, post_type: e.target.value })}>
              {SOCIAL_POST_TYPES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Voice</label>
            <select className="input-field" data-testid="social-tone" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })}>
              {SOCIAL_TONES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>
              Anything to emphasize? <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              className="input-field"
              data-testid="social-emphasis"
              placeholder="e.g. lead with the backyard and the remodeled kitchen; open house Saturday 11–2"
              value={form.emphasis}
              onChange={(e) => setForm({ ...form, emphasis: e.target.value })}
            />
          </div>
        </div>
        <button type="button" className="btn-primary" onClick={() => generate(null)} disabled={generating} style={{ marginTop: 16 }} data-testid="social-generate-btn">
          <Sparkles size={14} /> {generating ? "Writing…" : "Write caption"}
        </button>
      </div>

      {/* ----- Drafts ----- */}
      {posts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }} data-testid="social-post-list">
          {posts.map((post) => (
            <SocialPostCard
              key={post.post_id}
              post={post}
              photos={photos}
              onPatch={(patch) => patchPost(post.post_id, patch)}
              onRegenerate={() => generate(post.post_id)}
              onDelete={() => deletePost(post.post_id)}
              busy={generating}
              setToast={setToast}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SocialPostCard({ post, photos, onPatch, onRegenerate, onDelete, busy, setToast }) {
  const [caption, setCaption] = useState(post.caption);
  const [picking, setPicking] = useState(false);
  useEffect(() => { setCaption(post.caption); }, [post.caption]);

  const dirty = caption !== post.caption;
  const attached = post.photo_ids || [];
  const platformLabel = SOCIAL_PLATFORMS.find((x) => x.id === post.platform)?.label || post.platform;
  const typeLabel = SOCIAL_POST_TYPES.find((x) => x.id === post.post_type)?.label || post.post_type;
  const hashtagLine = (post.hashtags || []).map((h) => `#${h}`).join(" ");

  const copyAll = async () => {
    const text = [caption, hashtagLine].filter(Boolean).join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      setToast("Caption copied — paste it straight into the app.");
    } catch {
      setToast("Couldn't copy automatically. Select the text and copy it manually.");
    }
  };

  const togglePhoto = (photoId) => {
    const next = attached.includes(photoId) ? attached.filter((id) => id !== photoId) : [...attached, photoId];
    onPatch({ photo_ids: next });
  };

  return (
    <div className="card" style={{ padding: 20, maxWidth: 860 }} data-testid={`social-post-${post.post_id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className="badge">{platformLabel}</span>
          <span style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{typeLabel}</span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <button type="button" className="btn-ghost" onClick={copyAll} title="Copy caption + hashtags" data-testid={`social-copy-${post.post_id}`}>
            <Copy size={14} /> Copy
          </button>
          <button type="button" className="btn-ghost" onClick={onRegenerate} disabled={busy} title="Write a different version" data-testid={`social-regen-${post.post_id}`}>
            <RefreshCw size={14} /> Rewrite
          </button>
          <button type="button" className="btn-ghost" onClick={onDelete} title="Delete this draft" data-testid={`social-delete-${post.post_id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <textarea
        className="input-field"
        data-testid={`social-caption-${post.post_id}`}
        rows={Math.max(4, Math.min(14, caption.split("\n").length + 1))}
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        style={{ resize: "vertical", lineHeight: 1.6 }}
      />
      {dirty && (
        <button type="button" className="btn-secondary" onClick={() => onPatch({ caption })} style={{ marginTop: 8 }} data-testid={`social-save-${post.post_id}`}>
          <Save size={14} /> Save edit
        </button>
      )}

      {hashtagLine && (
        <div style={{ marginTop: 10, color: "var(--primary)", fontSize: 13, lineHeight: 1.7 }}>{hashtagLine}</div>
      )}

      <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
            Photos {attached.length ? `· ${attached.length} attached` : ""}
          </span>
          {photos.length > 0 && (
            <button type="button" className="btn-ghost" onClick={() => setPicking((v) => !v)} data-testid={`social-pick-${post.post_id}`}>
              {picking ? "Done" : attached.length ? "Change" : "Attach photos"}
            </button>
          )}
        </div>

        {photos.length === 0 ? (
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 8 }}>
            Upload photos above and you can attach them here.
          </div>
        ) : picking ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8, marginTop: 10 }}>
            {photos.map((ph) => {
              const on = attached.includes(ph.photo_id);
              return (
                <button
                  key={ph.photo_id}
                  type="button"
                  onClick={() => togglePhoto(ph.photo_id)}
                  data-testid={`social-photo-toggle-${post.post_id}-${ph.photo_id}`}
                  style={{
                    padding: 0, border: on ? "2px solid var(--primary)" : "1px solid var(--border)",
                    borderRadius: 8, overflow: "hidden", cursor: "pointer", background: "none", position: "relative",
                  }}
                >
                  <AuthImage photoId={ph.photo_id} alt={ph.filename} style={{ width: "100%", height: 68, objectFit: "cover", display: "block" }} />
                  {on && (
                    <span style={{
                      position: "absolute", bottom: 4, right: 4, background: "var(--primary)", color: "#fff",
                      borderRadius: "50%", width: 18, height: 18, fontSize: 11, lineHeight: "18px", textAlign: "center",
                    }}>✓</span>
                  )}
                </button>
              );
            })}
          </div>
        ) : attached.length ? (
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            {attached.map((id) => (
              <AuthImage key={id} photoId={id} alt="" style={{ width: 74, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border)" }} />
            ))}
          </div>
        ) : (
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 8 }}>None attached yet.</div>
        )}
      </div>
    </div>
  );
}


/* ---------- Buyer Feedback ---------- */

function FeedbackTab({ seller, setToast }) {
  const sellerId = seller?.id || seller?._id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const blank = { kind: "showing", date: new Date().toISOString().slice(0, 10), body: "", groups: 0, source: "", shared: true };
  const [nf, setNf] = useState(blank);

  const load = async () => {
    if (!sellerId) return;
    try {
      const { data } = await api.get(`/admin/sellers/${sellerId}/feedback`);
      setItems(data.feedback || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sellerId]);

  const add = async () => {
    if (!nf.body.trim()) { setToast("Write what the buyer said first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/feedback", { seller_id: sellerId, ...nf });
      setItems((xs) => [data.feedback, ...xs]);
      setNf({ ...blank, date: nf.date });
      setToast(nf.shared ? "Added — your seller can see it now." : "Added as a private note. Your seller won't see it.");
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't save that");
    } finally { setBusy(false); }
  };

  const patch = async (id, body) => {
    try {
      const { data } = await api.put(`/admin/feedback/${id}`, body);
      setItems((xs) => xs.map((f) => (f.feedback_id === id ? data.feedback : f)));
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't save that change");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback?")) return;
    await api.delete(`/admin/feedback/${id}`);
    setItems((xs) => xs.filter((f) => f.feedback_id !== id));
  };

  const shownCount = items.filter((f) => f.shared).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="card" style={{ padding: 24, maxWidth: 760 }}>
        <div className="overline">Add buyer feedback</div>
        <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, marginBottom: 16, maxWidth: 560 }}>
          What the buyer's agent told you after a showing, or how an open house went. Keep names out of it —
          your seller sees these, attributed to the brokerage rather than a person.
        </p>

        <div className="form-grid">
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Came from</label>
            <select data-testid="fb-kind" className="input-field" value={nf.kind}
                    onChange={(e) => setNf({ ...nf, kind: e.target.value })}>
              <option value="showing">Private showing</option>
              <option value="open_house">Open house</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Date</label>
            <input data-testid="fb-date" type="date" className="input-field" value={nf.date}
                   onChange={(e) => setNf({ ...nf, date: e.target.value })} />
          </div>
          {nf.kind === "open_house" && (
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Groups through</label>
              <input data-testid="fb-groups" type="number" min="0" className="input-field" value={nf.groups}
                     onChange={(e) => setNf({ ...nf, groups: Math.max(0, parseInt(e.target.value, 10) || 0) })} />
            </div>
          )}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>What they said</label>
            <textarea data-testid="fb-body" className="input-field" rows={3} value={nf.body}
                      onChange={(e) => setNf({ ...nf, body: e.target.value })}
                      style={{ resize: "vertical", lineHeight: 1.6 }}
                      placeholder="Loved the great room and the patio. Pre-approved, asked what other offers we've seen." />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>
              Attribution <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span>
            </label>
            <input data-testid="fb-source" className="input-field" value={nf.source}
                   onChange={(e) => setNf({ ...nf, source: e.target.value })}
                   placeholder="Shared by the buyer's agent" />
          </div>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 13.5, cursor: "pointer" }}>
          <input type="checkbox" data-testid="fb-shared" checked={nf.shared}
                 onChange={(e) => setNf({ ...nf, shared: e.target.checked })} />
          Show this to my seller
          <span style={{ color: "var(--text-muted)", fontSize: 12.5 }}>
            — uncheck to keep it as a private note for yourself
          </span>
        </label>

        <button type="button" className="btn-primary" onClick={add} disabled={busy} style={{ marginTop: 16 }} data-testid="fb-add">
          <Plus size={14} /> {busy ? "Saving…" : "Add feedback"}
        </button>
      </div>

      <div className="card" style={{ padding: 0, maxWidth: 760, overflow: "hidden" }} data-testid="fb-list">
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontWeight: 500 }}>Collected feedback</span>
          <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
            {items.length} total · {shownCount} visible to your seller
          </span>
        </div>

        {loading ? (
          <div style={{ padding: 24, color: "var(--text-muted)" }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 24, color: "var(--text-muted)" }} data-testid="fb-empty">
            Nothing yet. Add the first note after your next showing.
          </div>
        ) : items.map((f) => (
          <div key={f.feedback_id} data-testid={`fb-${f.feedback_id}`}
               style={{ padding: "16px 20px", borderTop: "1px solid var(--border)", opacity: f.shared ? 1 : 0.62 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span className="badge">{f.kind === "open_house" ? "Open house" : "Private showing"}</span>
                <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                  {f.date}{f.kind === "open_house" && f.groups ? ` · ${f.groups} groups` : ""}
                </span>
                {!f.shared && (
                  <span className="badge" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>
                    Private note
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button type="button" className="btn-ghost" data-testid={`fb-toggle-${f.feedback_id}`}
                        onClick={() => patch(f.feedback_id, { shared: !f.shared })}>
                  {f.shared ? "Hide from seller" : "Show to seller"}
                </button>
                <button type="button" className="btn-ghost" style={{ color: "var(--secondary)" }}
                        onClick={() => remove(f.feedback_id)} data-testid={`fb-del-${f.feedback_id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div style={{ marginTop: 8, lineHeight: 1.6 }}>{f.body}</div>
            {f.source && (
              <div style={{ marginTop: 6, color: "var(--text-muted)", fontSize: 12.5 }}>{f.source}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// `only` splits one component across two tabs: Showings lists private
// showings, Open Houses lists open houses. Same records underneath -- an
// open house is a showing with kind "open_house" plus a group count.
function ShowingsTab({ seller, only = "showing" }) {
  const isOH = only === "open_house";
  const NOUN = isOH ? "open house" : "showing";
  const [showings, setShowings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { ...showing } or null for new
  // `/admin/seller/{id}/personalization` returns `seller.id` while the
  // `/admin/sellers` list endpoint returns `seller._id`. Defensively accept
  // either so this tab works regardless of which parent loaded the seller.
  const sellerId = seller?.id || seller?._id;

  const load = async () => {
    if (!sellerId) return;
    try {
      const { data } = await api.get(`/admin/sellers/${sellerId}/showings`);
      setShowings(data.showings || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sellerId]);

  const remove = async (id) => {
    if (!window.confirm("Delete this showing? Reminders will not be sent.")) return;
    await api.delete(`/admin/showings/${id}`);
    setShowings((xs) => xs.filter((s) => s.id !== id));
  };

  const onSaved = (saved, isNew) => {
    setShowings((xs) => isNew ? [...xs, saved].sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`)) : xs.map((s) => s.id === saved.id ? saved : s));
    setEditing(null);
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  };
  const fmtTime = (t) => {
    if (!t) return "";
    const s = String(t).trim();
    // Not a strict HH:MM means the agent typed it that way ("4:30 PM",
    // "11am - 1pm"), so hand it back rather than bolting a second AM/PM on.
    if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) return s;
    const [hh, mm] = s.split(":");
    const hr = parseInt(hh, 10);
    const ampm = hr >= 12 ? "PM" : "AM";
    const h12 = ((hr + 11) % 12) + 1;
    return `${h12}:${mm} ${ampm}`;
  };

  const mine = showings.filter((s) => (s.kind || "showing") === only);
  const upcoming = mine.filter((s) => s.status !== "cancelled" && s.date >= new Date().toISOString().slice(0, 10));
  const past = mine.filter((s) => !upcoming.includes(s));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="card" style={{ padding: 24, maxWidth: 720, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div className="overline">{isOH ? "Open houses" : "Showings calendar"}</div>
          <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, maxWidth: 480 }}>
            {isOH ? (<>Log open houses here, including how many groups came through. Your seller sees the count on their <b>Home Activity</b> page.</>)
                   : (<>Add buyer showings here. The seller automatically gets an email <b>the night before at 6 PM</b> and again <b>2 hours before</b>.</>)}
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditing({ kind: only })} data-testid="add-showing-btn">
          <Plus size={14} /> Add {NOUN}
        </button>
      </div>

      {loading ? (
        <div style={{ color: "var(--text-muted)" }}>Loading…</div>
      ) : mine.length === 0 ? (
        <div className="card" style={{ padding: 32, color: "var(--text-muted)", maxWidth: 720 }} data-testid="showings-empty">
          No {NOUN}s yet. Click <b>+ Add {NOUN}</b> to schedule the first one.
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="card" style={{ overflow: "hidden", maxWidth: 720 }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)", fontWeight: 500 }}>Upcoming</div>
              {upcoming.map((s) => (
                <ShowingRow key={s.id} s={s} fmtDate={fmtDate} fmtTime={fmtTime} onEdit={() => setEditing(s)} onRemove={() => remove(s.id)} />
              ))}
            </div>
          )}
          {past.length > 0 && (
            <div className="card" style={{ overflow: "hidden", maxWidth: 720 }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)", fontWeight: 500, color: "var(--text-muted)" }}>Past</div>
              {past.map((s) => (
                <ShowingRow key={s.id} s={s} fmtDate={fmtDate} fmtTime={fmtTime} onEdit={() => setEditing(s)} onRemove={() => remove(s.id)} muted />
              ))}
            </div>
          )}
        </>
      )}

      {editing && (
        <ShowingEditor
          showing={editing}
          sellerId={sellerId}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function ShowingRow({ s, fmtDate, fmtTime, onEdit, onRemove, muted }) {
  return (
    <div data-testid={`showing-${s.id}`} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center", padding: "14px 20px", borderTop: "1px solid var(--border)", opacity: muted ? 0.7 : 1 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>
          {fmtDate(s.date)} · {fmtTime(s.start_time)}{s.end_time ? ` – ${fmtTime(s.end_time)}` : ""}
          {s.kind === "open_house" && s.groups ? <span className="badge" style={{ marginLeft: 8 }}>{s.groups} groups</span> : null}
          {s.status === "cancelled" && <span className="badge" style={{ marginLeft: 8, background: "var(--secondary)", color: "#fff" }}>Cancelled</span>}
          {s.status === "completed" && <span className="badge" style={{ marginLeft: 8 }}>Completed</span>}
        </div>
        <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 2 }}>
          {[s.buyer_agent, s.buyer_brokerage].filter(Boolean).join(" · ") || "—"}
          {s.note && ` · ${s.note}`}
        </div>
        <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 4 }}>
          Reminders: {s.reminder_eve_sent ? "✓ night-before" : "○ night-before"} · {s.reminder_2h_sent ? "✓ 2h" : "○ 2h"}
        </div>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        <button type="button" onClick={onEdit} className="btn-ghost" style={{ padding: 8 }} data-testid={`showing-edit-${s.id}`}>Edit</button>
        <button type="button" onClick={onRemove} className="btn-ghost" style={{ padding: 8, color: "var(--secondary)" }} data-testid={`showing-remove-${s.id}`}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function ShowingEditor({ showing, sellerId, onClose, onSaved }) {
  const isNew = !showing.id;
  const [f, setF] = useState({
    date: showing.date || "",
    start_time: showing.start_time || "",
    end_time: showing.end_time || "",
    buyer_agent: showing.buyer_agent || "",
    buyer_brokerage: showing.buyer_brokerage || "",
    note: showing.note || "",
    status: showing.status || "scheduled",
    kind: showing.kind || "showing",
    groups: showing.groups || 0,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    if (!f.date || !f.start_time) { setErr("Date and start time are required."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = isNew
        ? await api.post("/admin/showings", { seller_id: sellerId, ...f })
        : await api.put(`/admin/showings/${showing.id}`, f);
      onSaved(data.showing, isNew);
    } catch (e) {
      setErr(e.response?.data?.detail || "Couldn't save — try again.");
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,25,23,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 50 }} data-testid="showing-editor">
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: "100%", maxWidth: 540, padding: 24, background: "var(--bg)" }}>
        <h2 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "0 0 14px" }}>{isNew ? "New showing" : "Edit showing"}</h2>
        <div className="form-grid">
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Date</label>
            <input data-testid="showing-date" type="date" className="input-field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Start time</label>
            <input data-testid="showing-start" type="time" className="input-field" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>End time (optional)</label>
            <input data-testid="showing-end" type="time" className="input-field" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Status</label>
            <select className="input-field" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Type</label>
            <select data-testid="showing-kind" className="input-field" value={f.kind}
                    onChange={(e) => setF({ ...f, kind: e.target.value })}>
              <option value="showing">Private showing</option>
              <option value="open_house">Open house</option>
            </select>
          </div>
          {f.kind === "open_house" && (
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Groups through</label>
              <input data-testid="showing-groups" type="number" min="0" className="input-field"
                     value={f.groups}
                     onChange={(e) => setF({ ...f, groups: Math.max(0, parseInt(e.target.value, 10) || 0) })} />
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>Fill this in afterwards.</div>
            </div>
          )}
          {f.kind !== "open_house" && (<>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Buyer's agent (optional)</label>
            <input data-testid="showing-agent" className="input-field" value={f.buyer_agent} onChange={(e) => setF({ ...f, buyer_agent: e.target.value })} placeholder="Agent name" />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Brokerage (optional)</label>
            <input className="input-field" value={f.buyer_brokerage} onChange={(e) => setF({ ...f, buyer_brokerage: e.target.value })} />
          </div>
          </>)}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Notes (optional)</label>
            <input data-testid="showing-note" className="input-field" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Buyer is pre-approved cash, kids OK, etc." />
          </div>
        </div>
        {err && <div style={{ color: "var(--secondary)", fontSize: 13, marginTop: 10 }} data-testid="showing-err">{err}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 18, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} className="btn-ghost" disabled={busy}>Cancel</button>
          <button type="button" onClick={save} className="btn-primary" disabled={busy} data-testid="showing-save">
            <Save size={14} /> {busy ? "Saving…" : (isNew ? (f.kind === "open_house" ? "Schedule open house" : "Schedule showing") : "Save changes")}
          </button>
        </div>
      </div>
    </div>
  );
}


function VendorsTab({ p, save }) {
  const [vendors, setVendors] = useState(p.custom_vendors || []);
  const [nv, setNv] = useState({ category: "Cleaning", name: "", phone: "", email: "", note: "" });
  const add = () => {
    if (!nv.name.trim()) return;
    setVendors([...vendors, { ...nv, id: `tmp_${Date.now()}` }]);
    setNv({ category: "Cleaning", name: "", phone: "", email: "", note: "" });
  };
  const remove = (i) => setVendors(vendors.filter((_, j) => j !== i));
  const saveAll = () => save({ custom_vendors: vendors }, "Vendors saved.");

  return (
    <div>
      <div className="card" style={{ padding: 24, marginBottom: 16 }} data-testid="new-vendor-form">
        <div className="overline">Recommend a vendor</div>
        <div className="form-grid" style={{ marginTop: 12 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Category</label>
            <input data-testid="vendor-category" className="input-field" value={nv.category} onChange={(e) => setNv({ ...nv, category: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Name</label>
            <input data-testid="vendor-name" className="input-field" value={nv.name} onChange={(e) => setNv({ ...nv, name: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Phone</label>
            <input data-testid="vendor-phone" className="input-field" value={nv.phone} onChange={(e) => setNv({ ...nv, phone: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Email</label>
            <input data-testid="vendor-email" className="input-field" value={nv.email} onChange={(e) => setNv({ ...nv, email: e.target.value })} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Note</label>
            <input data-testid="vendor-note" className="input-field" value={nv.note} onChange={(e) => setNv({ ...nv, note: e.target.value })} placeholder="e.g. Ask for Maria, mention my name" />
          </div>
        </div>
        <button data-testid="vendor-add-btn" type="button" className="btn-secondary" onClick={add} style={{ marginTop: 16 }}>
          <Plus size={14} /> Add vendor
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="vendor-list">
        {vendors.length === 0 ? (
          <div style={{ padding: 24, color: "var(--text-muted)" }}>No custom vendors. The seller sees the default directory — add one here to personalize.</div>
        ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr style={{ background: "var(--surface-alt)" }}>
                {["Category", "Name", "Phone", "Email", "Note", ""].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "12px 16px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vendors.map((v, i) => (
                <tr key={v.id || i} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "12px 16px" }}><span className="badge">{v.category}</span></td>
                  <td style={{ padding: "12px 16px", fontWeight: 500 }}>{v.name}</td>
                  <td style={{ padding: "12px 16px", color: "var(--text-muted)" }}>{v.phone || "—"}</td>
                  <td style={{ padding: "12px 16px", color: "var(--text-muted)" }}>{v.email || "—"}</td>
                  <td style={{ padding: "12px 16px", color: "var(--text-muted)" }}>{v.note || "—"}</td>
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <button data-testid={`vendor-remove-${i}`} className="btn-ghost" onClick={() => remove(i)}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 20 }}>
        <button data-testid="vendors-save-btn" className="btn-primary" onClick={saveAll}>
          <Save size={14} /> Save changes
        </button>
      </div>
    </div>
  );
}

/* ---------- Net Sheet (admin pre-fill) ---------- */
const NS_FIELDS = [
  { key: "purchase_price", label: "Purchase / List Price", prefix: "$", type: "number", placeholder: "525000" },
  { key: "mortgage_payoff", label: "Mortgage Payoff Balance", prefix: "$", type: "number", placeholder: "250000" },
  { key: "concessions", label: "Seller Concessions to Buyer", prefix: "$", type: "number" },
  { key: "listing_commission_pct", label: "Listing Commission (%)", suffix: "%", type: "number", step: "0.1", placeholder: "3" },
  { key: "buyer_commission_pct", label: "Buyer Commission (%)", suffix: "%", type: "number", step: "0.1", placeholder: "3" },
  { key: "home_warranty", label: "Home Warranty", prefix: "$", type: "number" },
  { key: "hoa_transfer", label: "HOA Transfer Fee", prefix: "$", type: "number" },
  { key: "annual_property_taxes", label: "Annual Property Taxes", prefix: "$", type: "number", placeholder: "3500" },
  { key: "closing_date", label: "Estimated Closing Date", type: "date" },
];

function NetSheetTab({ seller, p, save, setToast }) {
  const [values, setValues] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const saveTimer = useRef(null);
  const loaded = useRef(false);

  // Load the seller's existing draft (writes from previous saves), with personalization fallback
  // for backward compat with the legacy "Net sheet defaults" card on the Dates tab.
  useEffect(() => {
    if (!seller?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/seller/${seller.id}/draft/net_sheet`);
        const draft = (data && data.data) ? data.data : {};
        const next = { ...draft };
        if (next.annual_property_taxes == null || next.annual_property_taxes === "") {
          if (p.annual_property_taxes != null) next.annual_property_taxes = String(p.annual_property_taxes);
        }
        if (!next.closing_date && p.net_sheet_closing_date) {
          next.closing_date = p.net_sheet_closing_date;
        }
        setValues(next);
      } catch {}
      loaded.current = true;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller?.id]);

  // Auto-save (debounced) so admin work isn't lost
  useEffect(() => {
    if (!loaded.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(`/admin/seller/${seller.id}/draft/net_sheet`, { data: values });
        setSavedAt(new Date());
      } catch {}
    }, 1000);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [values, seller?.id]);

  const set = (key) => (e) => setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const onSaveNow = async () => {
    setBusy(true);
    try {
      // Persist to draft (canonical source for new fields)
      await api.put(`/admin/seller/${seller.id}/draft/net_sheet`, { data: values });
      // Mirror the two legacy fields into personalization for older sellers.
      const legacyPatch = {};
      if (values.annual_property_taxes !== undefined && values.annual_property_taxes !== "") {
        legacyPatch.annual_property_taxes = Number(values.annual_property_taxes);
      }
      if (values.closing_date) legacyPatch.net_sheet_closing_date = values.closing_date;
      if (Object.keys(legacyPatch).length) await save(legacyPatch, "");
      setSavedAt(new Date());
      setToast("Net sheet pre-fill saved — your seller will see these values.");
    } catch (e) {
      setToast(formatApiError(e?.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-net-sheet-tab" style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 820 }}>
      <div className="card" style={{ padding: 28 }}>
        <div className="overline">Net sheet pre-fill</div>
        <h3 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "6px 0 4px", letterSpacing: "-0.01em" }}>
          Pre-fill {seller?.name?.split(" ")[0] || "your seller"}'s net sheet
        </h3>
        <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0 }}>
          Anything you fill in here shows up live on their <b>Net Sheet</b> tab. They can still edit it.
          Auto-saves as you type. Title/Escrow auto-computes as 0.5% × purchase price + $395 on the seller's view.
        </p>
        <div className="form-grid" style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          {NS_FIELDS.map((f) => (
            <div key={f.key}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>{f.label}</label>
              <div style={{ position: "relative" }}>
                {f.prefix && (
                  <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none", fontSize: 14 }}>{f.prefix}</span>
                )}
                <input
                  data-testid={`admin-ns-${f.key}`}
                  type={f.type}
                  step={f.step}
                  className="input-field"
                  value={values[f.key] ?? ""}
                  onChange={set(f.key)}
                  placeholder={f.placeholder}
                  style={f.prefix ? { paddingLeft: 28 } : undefined}
                />
                {f.suffix && (
                  <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none", fontSize: 14 }}>{f.suffix}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 28 }}>
        <div className="overline">Misc credits & debits (optional)</div>
        <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0" }}>
          Use these for anything custom — repair credits, prepaid HOA, special concessions, etc.
        </p>
        <div className="form-grid" style={{ marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Credit label</label>
            <input data-testid="admin-ns-misc-credit-label" type="text" className="input-field" value={values.misc_credit_label ?? ""} onChange={set("misc_credit_label")} placeholder="e.g. Prepaid HOA" />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Credit amount</label>
            <input data-testid="admin-ns-misc-credit-amount" type="number" className="input-field" value={values.misc_credit_amount ?? ""} onChange={set("misc_credit_amount")} placeholder="0" />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Debit label</label>
            <input data-testid="admin-ns-misc-debit-label" type="text" className="input-field" value={values.misc_debit_label ?? ""} onChange={set("misc_debit_label")} placeholder="e.g. Repair credit to buyer" />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Debit amount</label>
            <input data-testid="admin-ns-misc-debit-amount" type="number" className="input-field" value={values.misc_debit_amount ?? ""} onChange={set("misc_debit_amount")} placeholder="0" />
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ color: "var(--text-muted)", fontSize: 12 }} data-testid="admin-ns-saved-status">
          {savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : "Auto-saves as you type"}
        </div>
        <button data-testid="admin-ns-save-btn" className="btn-primary" onClick={onSaveNow} disabled={busy}>
          <Save size={14} /> {busy ? "Saving…" : "Save now"}
        </button>
      </div>
    </div>
  );
}

/* ---------- Documents (admin upload) ---------- */
const DOC_CATEGORIES = [
  { id: "listing_agreement", label: "Listing Agreement" },
  { id: "disclosures", label: "Seller Disclosures" },
  { id: "inspection", label: "Inspection Report" },
  { id: "appraisal", label: "Appraisal" },
  { id: "contract", label: "Purchase Contract" },
  { id: "addendum", label: "Addendum" },
  { id: "closing", label: "Closing Documents" },
  { id: "other", label: "Other" },
];
const DOC_LABEL = Object.fromEntries(DOC_CATEGORIES.map((c) => [c.id, c.label]));

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function DocumentsTab({ seller, setToast }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("disclosures");
  const [notes, setNotes] = useState("");
  // `has_hoa` is mirrored from the seller's personalization. When true,
  // the seller sees an HOA upload section on their /documents page and
  // a reminder card on their dashboard.
  const [hasHoa, setHasHoa] = useState(false);
  const [hoaToggleBusy, setHoaToggleBusy] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    if (!seller?.id) return;
    try {
      const [docsRes, perRes] = await Promise.all([
        api.get(`/admin/seller/${seller.id}/documents`),
        api.get(`/admin/seller/${seller.id}/personalization`),
      ]);
      setDocs(docsRes.data.documents || []);
      setHasHoa(!!perRes.data?.personalization?.has_hoa);
    } catch (e) {
      setToast(formatApiError(e?.response?.data?.detail) || "Could not load documents");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [seller?.id]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setToast("File is too large (max 25 MB).");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("notes", notes);
      await api.post(`/admin/seller/${seller.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNotes("");
      await load();
      setToast(`Uploaded — your seller can download it from their Documents tab.`);
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const removeDoc = async (doc_id, filename) => {
    if (!window.confirm(`Delete "${filename}"? Your seller will no longer be able to download it.`)) return;
    try {
      await api.delete(`/admin/documents/${doc_id}`);
      setDocs((xs) => xs.filter((d) => d.doc_id !== doc_id));
      setToast("Document deleted.");
    } catch (e) {
      setToast(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const updateCategory = async (doc_id, value) => {
    try {
      await api.patch(`/admin/documents/${doc_id}`, { category: value });
      setDocs((xs) => xs.map((d) => d.doc_id === doc_id ? { ...d, category: value } : d));
    } catch {}
  };

  const toggleHoa = async () => {
    const next = !hasHoa;
    setHoaToggleBusy(true);
    try {
      await api.put(`/admin/seller/${seller.id}/personalization`, { has_hoa: next });
      setHasHoa(next);
      setToast(next
        ? "HOA upload section turned on for this seller."
        : "HOA upload section turned off.");
    } catch (e) {
      setToast(formatApiError(e?.response?.data?.detail) || "Couldn't update HOA setting.");
    } finally {
      setHoaToggleBusy(false);
    }
  };

  return (
    <div data-testid="admin-documents-tab" style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 880 }}>
      {/* HOA toggle — when on, the seller can upload CC&Rs, rules, budget,
          and meeting minutes from their own /documents page, and sees a
          reminder card on their dashboard. */}
      <div className="card" data-testid="hoa-toggle-card" style={{ padding: 22, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 280, flex: 1 }}>
          <div className="overline">Seller-uploaded HOA packet</div>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "6px 0 0", lineHeight: 1.5 }}>
            Turn this on for sellers in an HOA. They'll see a place on their <b>Documents</b> page to upload CC&amp;Rs, rules &amp; regulations, budget &amp; financials, and recent meeting minutes — and a reminder on their dashboard until each section has at least one file.
          </p>
        </div>
        <button
          type="button"
          data-testid="hoa-toggle-btn"
          onClick={toggleHoa}
          disabled={hoaToggleBusy}
          className={hasHoa ? "btn-primary" : "btn-secondary"}
          style={{ minWidth: 140 }}
        >
          {hoaToggleBusy ? "Saving…" : hasHoa ? "HOA: On" : "HOA: Off"}
        </button>
      </div>

      <div className="card" style={{ padding: 28 }}>
        <div className="overline">Upload a document</div>
        <h3 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "6px 0 4px", letterSpacing: "-0.01em" }}>
          Share signed paperwork with {seller?.name?.split(" ")[0] || "your seller"}
        </h3>
        <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0 }}>
          PDFs, images, or Word docs (max 25 MB each). Anything you upload here shows up on your seller's <b>Documents</b> page where they can review and download any time.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginTop: 20 }}>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Category</label>
            <select
              data-testid="doc-upload-category"
              className="input-field"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {DOC_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Notes (optional)</label>
            <input
              data-testid="doc-upload-notes"
              className="input-field"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Signed disclosures from 4/15"
            />
          </div>
        </div>
        <div style={{ marginTop: 18 }}>
          <input
            ref={fileInputRef}
            data-testid="doc-upload-input"
            type="file"
            onChange={upload}
            style={{ display: "none" }}
            accept=".pdf,.png,.jpg,.jpeg,.heic,.doc,.docx,.txt"
          />
          <button
            data-testid="doc-upload-btn"
            className="btn-primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            <UploadIcon size={14} /> {busy ? "Uploading…" : "Choose file & upload"}
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 28 }}>
        <div className="overline">Uploaded documents ({docs.length})</div>
        {docs.length === 0 ? (
          <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 12 }}>
            No documents yet. Upload signed contracts, disclosures, inspections, etc., and they'll appear here for your seller to download.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
            {docs.map((d) => (
              <div key={d.doc_id} data-testid={`doc-row-${d.doc_id}`} style={{ padding: 16, border: "1px solid var(--border)", borderRadius: 12, display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.filename}</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
                    <select
                      data-testid={`doc-category-${d.doc_id}`}
                      className="input-field"
                      style={{ padding: "4px 8px", fontSize: 12, height: 28, width: "auto" }}
                      value={d.category}
                      onChange={(e) => updateCategory(d.doc_id, e.target.value)}
                    >
                      {DOC_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                    <span style={{ color: "var(--text-muted)", fontSize: 12 }}>{fmtBytes(d.size)}</span>
                    <span style={{ color: "var(--text-muted)", fontSize: 12 }}>· {new Date(d.uploaded_at).toLocaleDateString()}</span>
                    {Array.isArray(d.read_by) && d.read_by.length > 0 ? (
                      <span data-testid={`doc-read-${d.doc_id}`} title={d.read_by.map((r) => `${r.name || "Seller"} — ${new Date(r.at).toLocaleString()}`).join("\n")}
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--primary)",
                                     background: "color-mix(in srgb, var(--primary) 10%, transparent)", padding: "2px 8px", borderRadius: 999, fontWeight: 500 }}>
                        <Eye size={11} /> Opened by {d.read_by.map((r) => (r.name || "Seller").split(" ")[0]).join(", ")} · {new Date(d.read_by[0].at).toLocaleDateString()}
                      </span>
                    ) : (
                      <span data-testid={`doc-unread-${d.doc_id}`}
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--text-muted)",
                                     border: "1px dashed var(--border)", padding: "2px 8px", borderRadius: 999 }}>
                        Not opened yet
                      </span>
                    )}
                  </div>
                  {d.notes && <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 6 }}>{d.notes}</div>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <a
                    data-testid={`doc-download-${d.doc_id}`}
                    className="btn-secondary"
                    style={{ padding: "8px 12px", fontSize: 13 }}
                    href={`${API}/documents/${d.doc_id}/download`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Download size={13} />
                  </a>
                  <button
                    data-testid={`doc-delete-${d.doc_id}`}
                    className="btn-ghost"
                    style={{ padding: "8px 12px", fontSize: 13 }}
                    onClick={() => removeDoc(d.doc_id, d.filename)}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Listing Form (admin pre-fill) ---------- */
function ListingFormTab({ seller, setToast }) {
  const [reloadKey, setReloadKey] = useState(0);
  const [autofillResult, setAutofillResult] = useState(null); // {kind:'ok'|'err', message}
  const fileRef = useRef(null);
  const [autofilling, setAutofilling] = useState(false);
  const [downloading, setDownloading] = useState(false);
  if (!seller) return null;

  const downloadDraftPdf = async () => {
    setDownloading(true);
    try {
      // Fetch the latest draft directly from the server (most up-to-date) and build
      // rendered_sections client-side from the schema.
      const draftResp = await api.get(`/admin/seller/${seller.id}/draft/listing_input`);
      const values = draftResp.data?.data || {};
      const rendered_sections = buildRenderedSections(values);
      const pdfResp = await api.post(
        `/admin/seller/${seller.id}/draft/listing_input/pdf`,
        { ...values, rendered_sections },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([pdfResp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(seller.name || "seller").replace(/ /g, "_")}_listing_DRAFT.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setToast("Draft PDF downloaded.");
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't download draft PDF");
    } finally {
      setDownloading(false);
    }
  };

  const onAutofill = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setAutofillResult({ kind: "err", message: "PDF too large (max 10 MB)." });
      return;
    }
    setAutofilling(true);
    setAutofillResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/seller/${seller.id}/listing/autofill-from-pdf`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 },
      );
      setAutofillResult({
        kind: "ok",
        message: `Auto-filled ${data.applied_count} field${data.applied_count === 1 ? "" : "s"} from your PDF. Scroll through each step below to review — anything we couldn't extract is left blank for you.`,
      });
      setToast(`Auto-filled ${data.applied_count} fields. Review below.`);
      setReloadKey((k) => k + 1);
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || err?.message || "Auto-fill failed";
      setAutofillResult({ kind: "err", message: msg });
      setToast(`Auto-fill failed: ${msg}`);
    } finally {
      setAutofilling(false);
    }
  };

  return (
    <div data-testid="admin-listing-form-tab">
      <div className="card" style={{ padding: 20, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="overline" style={{ marginBottom: 4 }}>Auto-fill from MLS PDF</div>
          <p style={{ color: "var(--text-muted)", fontSize: 13, margin: 0, lineHeight: 1.5 }}>
            Drop in an old MLS data sheet and AI will pre-fill what it can recognize. Anything you've already typed is preserved. Takes 20–60 seconds.
          </p>
        </div>
        <input
          ref={fileRef}
          data-testid="autofill-pdf-input"
          type="file"
          accept="application/pdf,.pdf"
          onChange={onAutofill}
          style={{ display: "none" }}
        />
        <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
          <button
            data-testid="autofill-pdf-btn"
            className="btn-secondary"
            onClick={() => fileRef.current?.click()}
            disabled={autofilling}
          >
            <UploadIcon size={14} /> {autofilling ? "Reading PDF…" : "Upload MLS PDF"}
          </button>
          <button
            data-testid="download-draft-pdf-btn"
            className="btn-secondary"
            onClick={downloadDraftPdf}
            disabled={downloading}
            title="Download a PDF of whatever's in the draft right now — even if not submitted"
          >
            <Download size={14} /> {downloading ? "Generating…" : "Download draft as PDF"}
          </button>
        </div>
      </div>

      {autofilling && (
        <div data-testid="autofill-progress" style={{ marginBottom: 12, padding: "12px 16px", borderRadius: 12, background: "var(--surface-alt)", border: "1px solid var(--border)", color: "var(--text)", fontSize: 13 }}>
          Reading the PDF and extracting fields… this can take up to a minute on a long listing sheet.
        </div>
      )}

      {autofillResult && autofillResult.kind === "ok" && (
        <div data-testid="autofill-success" style={{ marginBottom: 12, padding: "14px 18px", borderRadius: 12, background: "#E8F0EA", border: "1px solid #0F5132", color: "#0F5132", fontSize: 14, lineHeight: 1.5 }}>
          ✓ {autofillResult.message}
        </div>
      )}
      {autofillResult && autofillResult.kind === "err" && (
        <div data-testid="autofill-error" style={{ marginBottom: 12, padding: "14px 18px", borderRadius: 12, background: "#F7E2E2", border: "1px solid #C04545", color: "#7A1F1F", fontSize: 14, lineHeight: 1.5 }}>
          ⚠ {autofillResult.message}
        </div>
      )}

      <ListingInputForm
        reloadKey={reloadKey}
        draftEndpoint={`/admin/seller/${seller.id}/draft/listing_input`}
        adminMode={true}
        sellerName={seller.name || ""}
        onAdminSaved={() => setToast("Pre-filled fields saved — your seller will see them.")}
        onSubmitted={() => {}}
      />
    </div>
  );
}

/* ---------- Submissions ---------- */
const REMINDER_FORM_LABELS = {
  listing_input: "Listing Input Form",
  home_story: "Your Home's Story",
  showing_preferences: "Showing Preferences",
};
const REMINDER_FORM_KEYS = Object.keys(REMINDER_FORM_LABELS);

// Human-friendly question labels for the qualitative form answers. Keeps the inline
// preview readable without relying on the PDF.
const HOME_STORY_QUESTIONS = [
  { name: "changes_since_purchase", label: "Changes since purchase" },
  { name: "favorite_home", label: "Favorite thing about the home" },
  { name: "favorite_neighborhood", label: "Favorite thing about the neighborhood" },
  { name: "special_features", label: "Special features to highlight" },
];
const SHOWING_FIELDS = [
  { name: "approval_mode", label: "Approval mode" },
  { name: "advance_notice", label: "Advance notice" },
  { name: "showing_window", label: "Showing window" },
  { name: "notify_phone", label: "Phone for texts" },
  { name: "notify_email", label: "Email notifications", type: "bool" },
  { name: "notify_text", label: "Text notifications", type: "bool" },
  { name: "instructions", label: "Special instructions" },
];

function AnswersInline({ formType, data }) {
  // Decide what fields to render based on the form type. For anything unrecognized,
  // fall back to a generic key/value dump so nothing gets hidden.
  const fields = (
    formType === "home_story" ? HOME_STORY_QUESTIONS :
    formType === "showing_preferences" ? SHOWING_FIELDS :
    null
  );
  const render = (f, v) => {
    if (f.type === "bool") return v ? "Yes" : "No";
    if (v === undefined || v === null || v === "") return <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>— (blank)</span>;
    return <span style={{ whiteSpace: "pre-wrap" }}>{String(v)}</span>;
  };
  if (fields) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
        {fields.map((f) => (
          <div key={f.name} data-testid={`answer-${f.name}`}>
            <div className="overline" style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>{f.label}</div>
            <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text)" }}>{render(f, data?.[f.name])}</div>
          </div>
        ))}
      </div>
    );
  }
  // Generic fallback for listing_input and anything else — show top-level scalar fields.
  const entries = Object.entries(data || {}).filter(([, v]) => (
    v !== undefined && v !== null && v !== "" && !Array.isArray(v) && typeof v !== "object"
  ));
  if (entries.length === 0) {
    return (
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)", fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>
        No text answers to preview — download the PDF to see the full submission.
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
      {entries.slice(0, 40).map(([k, v]) => (
        <div key={k}>
          <div className="overline" style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>{k.replace(/_/g, " ")}</div>
          <div style={{ fontSize: 13, color: "var(--text)", wordBreak: "break-word" }}>{String(v)}</div>
        </div>
      ))}
      {entries.length > 40 && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", gridColumn: "1 / -1" }}>
          Showing first 40 fields — download the PDF for the complete form.
        </div>
      )}
    </div>
  );
}

function SubmissionsTab({ items, setItems, setToast, sellerId }) {
  const [busyId, setBusyId] = useState("");
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [remindersLoaded, setRemindersLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!sellerId) return;
      try {
        const { data } = await api.get(`/admin/seller/${sellerId}/form-reminders`);
        if (alive) setReminders(data.reminders || []);
      } catch {
        // silent — nothing to show if the history endpoint fails
      } finally {
        if (alive) setRemindersLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [sellerId]);

  const submittedKeys = new Set(items.map((x) => x.form_type));
  const outstanding = REMINDER_FORM_KEYS.filter((k) => !submittedKeys.has(k));
  const lastReminderAt = reminders[0]?.sent_at;

  const onDelete = async (s) => {
    if (!window.confirm(
      `Delete this "${s.form_title}" submission?\n\n` +
      "Their draft stays filled in. The form on their side will go back to 'Not yet' so they can finish and resubmit. " +
      "This can't be undone."
    )) return;
    setBusyId(s.submission_id);
    try {
      await api.delete(`/admin/submissions/${s.submission_id}`);
      setItems((xs) => xs.filter((x) => x.submission_id !== s.submission_id));
      setToast?.("Submission deleted. Their form is back to 'Not yet' so they can finish.");
    } catch (e) {
      setToast?.(formatApiError(e?.response?.data?.detail) || "Couldn't delete submission.");
    } finally {
      setBusyId("");
    }
  };

  const onSendReminder = async () => {
    if (outstanding.length === 0) return;
    if (!window.confirm(
      `Email a friendly nudge to this seller about ${outstanding.length} unfinished form${outstanding.length === 1 ? "" : "s"}?`
    )) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/seller/${sellerId}/send-form-reminder`, { note: note.trim() || null });
      setToast?.(`Reminder sent to ${data.recipients.join(", ")}.`);
      setNote("");
      // refresh history
      const { data: h } = await api.get(`/admin/seller/${sellerId}/form-reminders`);
      setReminders(h.reminders || []);
    } catch (e) {
      setToast?.(formatApiError(e?.response?.data?.detail) || "Couldn't send the reminder.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Reminder panel — always visible; disabled (with helpful copy) when all 3 are submitted */}
      <div className="card" style={{ padding: 24 }} data-testid="reminder-panel">
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--primary)" }}>
          <Bell size={16} />
          <span className="overline" style={{ color: "var(--primary)" }}>Form reminder</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginTop: 6, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <h3 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "2px 0 4px" }}>
              {outstanding.length === 0
                ? "All forms submitted 🎉"
                : `${outstanding.length} form${outstanding.length === 1 ? "" : "s"} left to finish`}
            </h3>
            <div style={{ color: "var(--text-muted)", fontSize: 14 }}>
              {outstanding.length === 0
                ? "Nothing to nudge about right now. If you delete a submission, the reminder option will come back."
                : (
                  <>
                    Send a friendly email prompting them to open <b>{outstanding.map((k) => REMINDER_FORM_LABELS[k]).join(", ")}</b>.
                    Their drafts stay intact; the email just links them back to /forms.
                  </>
                )}
            </div>
            {lastReminderAt && (
              <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 8, fontStyle: "italic" }} data-testid="reminder-last-sent">
                Last reminder sent {new Date(lastReminderAt).toLocaleString()}.
              </div>
            )}
          </div>
        </div>

        {outstanding.length > 0 && (
          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>
              Optional personal note <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>— shown above the CTA in the email.</span>
            </label>
            <textarea
              data-testid="reminder-note"
              className="input-field"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder={`e.g. "No rush, but I'd love to get your listing live by next week — these just take 10 minutes."`}
            />
            <div>
              <button
                type="button"
                className="btn-primary"
                onClick={onSendReminder}
                disabled={sending}
                data-testid="reminder-send-btn"
              >
                <Send size={14} /> {sending ? "Sending…" : "Send reminder email"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Submitted forms grid */}
      {items.length === 0 ? (
        <div className="card" style={{ padding: 32, color: "var(--text-muted)" }}>No submissions yet from this seller.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
          {items.map((s) => {
            const isOpen = openId === s.submission_id;
            return (
              <div key={s.submission_id} className="card" style={{ padding: 24 }} data-testid={`admin-sub-${s.submission_id}`}>
                <div className="overline">{new Date(s.created_at).toLocaleString()}</div>
                <h3 className="font-serif" style={{ fontSize: 22, fontWeight: 500, marginTop: 4 }}>{s.form_title}</h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
                  <button
                    type="button"
                    data-testid={`admin-sub-view-${s.submission_id}`}
                    className="btn-secondary"
                    onClick={() => setOpenId(isOpen ? null : s.submission_id)}
                    style={{ padding: "8px 16px", fontSize: 14 }}
                  >
                    {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}{" "}
                    {isOpen ? "Hide answers" : "View answers"}
                  </button>
                  <a data-testid={`admin-pdf-${s.submission_id}`} className="btn-ghost" style={{ padding: "8px 16px", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6, color: "var(--primary)" }} href={`${API}/forms/submission/${s.submission_id}/pdf`} target="_blank" rel="noreferrer">
                    <Download size={14} /> PDF
                  </a>
                  <button
                    type="button"
                    data-testid={`admin-sub-delete-${s.submission_id}`}
                    className="btn-ghost"
                    onClick={() => onDelete(s)}
                    disabled={busyId === s.submission_id}
                    title="Delete this submission — the form on their side will go back to 'Not yet' so they can finish."
                    style={{ padding: "8px 14px", fontSize: 14, color: "var(--error)", display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    <Trash2 size={14} /> {busyId === s.submission_id ? "Deleting…" : "Delete"}
                  </button>
                </div>
                {isOpen && <AnswersInline formType={s.form_type} data={s.data} />}
              </div>
            );
          })}
        </div>
      )}

      {/* Reminder history (only shown when there's something to show) */}
      {remindersLoaded && reminders.length > 0 && (
        <details className="card" style={{ padding: 20 }} data-testid="reminder-history">
          <summary style={{ cursor: "pointer", fontSize: 14, color: "var(--text)" }}>
            Reminder history ({reminders.length})
          </summary>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
            {reminders.map((r) => (
              <div key={r.id} style={{ fontSize: 13, color: "var(--text-muted)", paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>
                <div><b style={{ color: "var(--text)" }}>{new Date(r.sent_at).toLocaleString()}</b> · sent to {r.sent_to.join(", ")}</div>
                <div style={{ fontSize: 12, marginTop: 2 }}>
                  {(r.outstanding_forms || []).map((k) => REMINDER_FORM_LABELS[k] || k).join(" · ")}
                </div>
                {r.note && <div style={{ marginTop: 4, fontStyle: "italic" }}>"{r.note}"</div>}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/* ---------- Buyer Wishlist (admin read-only view of the shortlist) ---------- */
const WL_STATUS_ADMIN = {
  loved:  { label: "Loved it", color: "#2B7A4B", bg: "#E7F0EA" },
  maybe:  { label: "Maybe",    color: "#B8860B", bg: "#F6EFD9" },
  passed: { label: "Passed",   color: "#9A4A3A", bg: "#F5E4E0" },
};

function AdminWishlistPhoto({ photoId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true; let objUrl = null;
    api.get(`/wishlist/photo/${photoId}`, { responseType: "blob" })
      .then((r) => { if (!alive) return; objUrl = URL.createObjectURL(r.data); setUrl(objUrl); })
      .catch(() => {});
    return () => { alive = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [photoId]);
  return (
    <a href={url || undefined} target="_blank" rel="noreferrer" data-testid={`admin-wishlist-photo-${photoId}`}
      style={{ width: 88, height: 88, borderRadius: 10, overflow: "hidden", background: "var(--surface-alt)", border: "1px solid var(--border)", flexShrink: 0, display: "block" }}>
      {url && <img src={url} alt="toured home" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
    </a>
  );
}

function WishlistViewTab({ sellerId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get(`/admin/seller/${sellerId}/wishlist`)
      .then((r) => { if (alive) setItems(r.data.items || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sellerId]);

  if (loading) return <div className="card" style={{ padding: 20, color: "var(--text-muted)" }}>Loading wishlist…</div>;
  if (items.length === 0) {
    return (
      <div className="card" data-testid="admin-wishlist-empty" style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>
        <Heart size={22} style={{ marginBottom: 10, color: "var(--primary)" }} />
        <div style={{ fontSize: 15 }}>This buyer hasn&rsquo;t saved any homes yet.</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>Homes they add from their portal&rsquo;s Home Wishlist tab will show up here.</div>
      </div>
    );
  }
  return (
    <div data-testid="admin-wishlist-tab" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "0 0 4px" }}>
        {items.length} home{items.length === 1 ? "" : "s"} on this buyer&rsquo;s shortlist, newest first.
      </p>
      {items.map((it) => {
        const s = WL_STATUS_ADMIN[it.status] || WL_STATUS_ADMIN.maybe;
        return (
          <div key={it.item_id} className="card" data-testid={`admin-wishlist-item-${it.item_id}`} style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div className="font-serif" style={{ fontSize: 20, fontWeight: 500, letterSpacing: "-0.01em" }}>{it.title || "Untitled home"}</div>
                {it.address && <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 3, display: "inline-flex", gap: 6, alignItems: "center" }}><Home size={13} /> {it.address}</div>}
              </div>
              <span style={{ flexShrink: 0, padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: s.bg, color: s.color }}>{s.label}</span>
            </div>
            {it.notes && <p style={{ color: "var(--text)", fontSize: 14, lineHeight: 1.55, margin: 0, whiteSpace: "pre-wrap" }}>{it.notes}</p>}
            {(it.photos || []).length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {it.photos.map((p) => <AdminWishlistPhoto key={p.photo_id} photoId={p.photo_id} />)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}



/* ================= Per-buyer override editors (BUYING side) =================
   Each edits a personalization override that layers on top of the agent's
   global Content CMS. Empty override ({} / []) = "use my global default". */

const LENDER_FIELDS = [
  { key: "name", label: "Loan officer name", ph: "e.g. Jamie Rivera" },
  { key: "title", label: "Title", ph: "e.g. Senior Loan Officer" },
  { key: "company", label: "Company", ph: "e.g. Anchor Home Lending" },
  { key: "nmls", label: "NMLS #", ph: "e.g. 123456" },
  { key: "phone", label: "Phone", ph: "e.g. (555) 123-4567" },
  { key: "email", label: "Email", ph: "e.g. jamie@anchorlending.com" },
];

function OverrideBanner({ overridden, defaultLabel, customLabel }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, letterSpacing: "0.03em",
      background: overridden ? "#F3ECDF" : "var(--surface-alt)", color: overridden ? "var(--secondary)" : "var(--text-muted)", border: "1px solid var(--border)" }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: overridden ? "var(--secondary)" : "var(--text-muted)" }} />
      {overridden ? customLabel : defaultLabel}
    </div>
  );
}

function BuyerLenderTab({ p, save }) {
  const { content } = useContent();
  const globalLender = { ...(DEFAULT_CONTENT.lender || {}), ...(content?.lender || {}) };
  const overridden = p?.lender_override && Object.keys(p.lender_override).length > 0;
  const [form, setForm] = useState(overridden ? { ...globalLender, ...p.lender_override } : { ...globalLender });
  const [busy, setBusy] = useState(false);

  const onSave = async () => {
    setBusy(true);
    const payload = {};
    LENDER_FIELDS.forEach(({ key }) => { if ((form[key] || "").trim()) payload[key] = form[key].trim(); });
    await save({ lender_override: payload }, "Lender saved for this buyer.");
    setBusy(false);
  };
  const onReset = async () => {
    setBusy(true);
    await save({ lender_override: {} }, "Reset to your global lender.");
    setForm({ ...globalLender });
    setBusy(false);
  };

  return (
    <div className="card" style={{ padding: 28 }} data-testid="buyer-lender-tab">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
        <div>
          <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Preferred lender</h2>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0", maxWidth: 560 }}>
            Set a specific lender for this buyer. Leave blank to use your global lender from Content.
          </p>
        </div>
        <OverrideBanner overridden={overridden} defaultLabel="Using your global lender" customLabel="Custom lender for this buyer" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
        {LENDER_FIELDS.map(({ key, label, ph }) => (
          <div key={key}>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>{label}</label>
            <input data-testid={`buyer-lender-${key}`} className="input-field" value={form[key] || ""} placeholder={globalLender[key] || ph} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
        <button type="button" className="btn-primary" disabled={busy} onClick={onSave} data-testid="buyer-lender-save" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Save size={15} /> Save lender
        </button>
        {overridden && (
          <button type="button" className="btn-ghost" disabled={busy} onClick={onReset} data-testid="buyer-lender-reset" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--text-muted)" }}>
            <RotateCcw size={14} /> Use global lender
          </button>
        )}
      </div>
    </div>
  );
}

function BuyerProcessTab({ p, save }) {
  const { content } = useContent();
  const clone = (x) => JSON.parse(JSON.stringify(x || []));
  const globalStages = (Array.isArray(content?.buyer_timeline) && content.buyer_timeline.length > 0)
    ? content.buyer_timeline : (DEFAULT_CONTENT.buyer_timeline || []);
  const overridden = Array.isArray(p?.buyer_timeline_override) && p.buyer_timeline_override.length > 0;
  const [stages, setStages] = useState(overridden ? clone(p.buyer_timeline_override) : clone(globalStages));
  const [busy, setBusy] = useState(false);

  const update = (i, patch) => setStages(stages.map((s, j) => j === i ? { ...s, ...patch } : s));
  const remove = (i) => setStages(stages.filter((_, j) => j !== i));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= stages.length) return; const n = [...stages]; [n[i], n[j]] = [n[j], n[i]]; setStages(n); };
  const add = () => setStages([...stages, { id: `stage_${Date.now().toString(36).slice(-4)}`, title: "New step", summary: "", details: "" }]);

  const onSave = async () => { setBusy(true); await save({ buyer_timeline_override: stages }, "Process saved for this buyer."); setBusy(false); };
  const onReset = async () => { setBusy(true); await save({ buyer_timeline_override: [] }, "Reset to your global process."); setStages(clone(globalStages)); setBusy(false); };

  return (
    <div className="card" style={{ padding: 28 }} data-testid="buyer-process-tab">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
        <div>
          <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Home-buying process</h2>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0", maxWidth: 560 }}>
            Tailor the journey steps this buyer sees. Save to override; reset to fall back to your global process.
          </p>
        </div>
        <OverrideBanner overridden={overridden} defaultLabel="Using your global process" customLabel="Custom process for this buyer" />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 16 }}>
        {stages.map((s, i) => (
          <div key={i} data-testid={`buyer-process-stage-${i}`} style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 16, background: "#FBFAF6" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Step {i + 1} title</label>
                <input data-testid={`buyer-process-title-${i}`} className="input-field" value={s.title || ""} onChange={(e) => update(i, { title: e.target.value })} placeholder="e.g. Get pre-approved" />
              </div>
              <button type="button" className="btn-ghost" style={{ padding: 8 }} onClick={() => move(i, -1)} disabled={i === 0}><ChevronUp size={14} /></button>
              <button type="button" className="btn-ghost" style={{ padding: 8 }} onClick={() => move(i, +1)} disabled={i === stages.length - 1}><ChevronDown size={14} /></button>
              <button type="button" className="btn-ghost" style={{ padding: 8, color: "var(--secondary)" }} onClick={() => remove(i)} data-testid={`buyer-process-remove-${i}`}><Trash2 size={14} /></button>
            </div>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Short summary</label>
            <input className="input-field" value={s.summary || ""} onChange={(e) => update(i, { summary: e.target.value })} placeholder="One line shown on the card" style={{ marginBottom: 10 }} />
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Details</label>
            <textarea className="input-field" rows={2} value={s.details || ""} onChange={(e) => update(i, { details: e.target.value })} placeholder="Expanded explanation" />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 16, alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" className="btn-secondary" onClick={add} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Plus size={14} /> Add step</button>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn-primary" disabled={busy} onClick={onSave} data-testid="buyer-process-save" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Save size={15} /> Save process</button>
        {overridden && (
          <button type="button" className="btn-ghost" disabled={busy} onClick={onReset} data-testid="buyer-process-reset" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--text-muted)" }}><RotateCcw size={14} /> Use global process</button>
        )}
      </div>
    </div>
  );
}

function BuyerIntakeTab({ p, save }) {
  const { content } = useContent();
  const clone = (x) => JSON.parse(JSON.stringify(x || []));
  const globalIntake = (Array.isArray(content?.buyer_intake_sections) && content.buyer_intake_sections.length > 0)
    ? content.buyer_intake_sections : (DEFAULT_CONTENT.buyer_intake_sections || []);
  const globalPref = (Array.isArray(content?.buyer_preferences_sections) && content.buyer_preferences_sections.length > 0)
    ? content.buyer_preferences_sections : (DEFAULT_CONTENT.buyer_preferences_sections || []);
  const intakeOver = Array.isArray(p?.buyer_intake_sections_override) && p.buyer_intake_sections_override.length > 0;
  const prefOver = Array.isArray(p?.buyer_preferences_sections_override) && p.buyer_preferences_sections_override.length > 0;

  const [intake, setIntake] = useState(intakeOver ? clone(p.buyer_intake_sections_override) : clone(globalIntake));
  const [pref, setPref] = useState(prefOver ? clone(p.buyer_preferences_sections_override) : clone(globalPref));
  const [busy, setBusy] = useState(false);

  const onSave = async () => {
    setBusy(true);
    // Send only the form that is actually this buyer's own. Sending both
    // would pin the untouched one to today's global version, quietly cutting
    // it off from any later change to the global form - a freeze nobody
    // asked for and nothing on screen would admit to.
    const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
    const patch = {};
    if (intakeOver || !same(intake, globalIntake)) patch.buyer_intake_sections_override = intake;
    if (prefOver || !same(pref, globalPref)) patch.buyer_preferences_sections_override = pref;
    if (!Object.keys(patch).length) { setBusy(false); return; }
    await save(patch, "Intake forms saved for this buyer.");
    setBusy(false);
  };
  const resetIntake = async () => { setBusy(true); await save({ buyer_intake_sections_override: [] }, "Reset intake to your global form."); setIntake(clone(globalIntake)); setBusy(false); };
  const resetPref = async () => { setBusy(true); await save({ buyer_preferences_sections_override: [] }, "Reset preferences to your global form."); setPref(clone(globalPref)); setBusy(false); };

  return (
    <div data-testid="buyer-intake-tab" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <OverrideBanner overridden={intakeOver} defaultLabel="Intake: using your global form" customLabel="Intake: custom for this buyer" />
        <OverrideBanner overridden={prefOver} defaultLabel="Preferences: using your global form" customLabel="Preferences: custom for this buyer" />
      </div>
      <SectionedFormEditor
        formKey="buyer_intake"
        title="Buyer intake form"
        intro="The questions this specific buyer answers when they first come on board."
        sections={intake}
        onChange={setIntake}
        onReset={intakeOver ? resetIntake : null}
        resetLabel="Use my global intake"
      />
      <SectionedFormEditor
        formKey="buyer_preferences"
        title="What they're looking for"
        intro="This buyer's home-search preferences questionnaire."
        sections={pref}
        onChange={setPref}
        onReset={prefOver ? resetPref : null}
        resetLabel="Use my global preferences"
      />
      <div style={{ position: "sticky", bottom: 16, display: "flex", justifyContent: "flex-end" }}>
        <button type="button" className="btn-primary" disabled={busy} onClick={onSave} data-testid="buyer-intake-save" style={{ display: "inline-flex", alignItems: "center", gap: 8, boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }}>
          <Save size={15} /> Save intake forms
        </button>
      </div>
    </div>
  );
}

function BuyerClosingTab({ p, save }) {
  const { content } = useContent();
  const clone = (x) => JSON.parse(JSON.stringify(x || []));
  const globalClosing = (Array.isArray(content?.buyer_closing) && content.buyer_closing.length > 0)
    ? content.buyer_closing : (DEFAULT_CONTENT.buyer_closing || []);
  const overridden = Array.isArray(p?.buyer_closing_override) && p.buyer_closing_override.length > 0;
  const [items, setItems] = useState(overridden ? clone(p.buyer_closing_override) : clone(globalClosing));
  const [busy, setBusy] = useState(false);

  const onSave = async () => { setBusy(true); await save({ buyer_closing_override: items }, "Closing checklist saved for this buyer."); setBusy(false); };
  const onReset = async () => { setBusy(true); await save({ buyer_closing_override: [] }, "Reset to your global closing checklist."); setItems(clone(globalClosing)); setBusy(false); };

  return (
    <div data-testid="buyer-closing-tab" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Closing-day checklist</h2>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0", maxWidth: 560 }}>
            Tailor the closing-day reminders this buyer sees on their Contract Timeline. Save to override; reset to fall back to your global checklist.
          </p>
        </div>
        <OverrideBanner overridden={overridden} defaultLabel="Using your global checklist" customLabel="Custom checklist for this buyer" />
      </div>
      <ChecklistEditor
        title="Checklist items"
        items={items}
        onChange={setItems}
        idPrefix="bc"
        testidPrefix="buyer-closing"
        addLabel="Add item"
      />
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        {overridden && (
          <button type="button" className="btn-ghost" disabled={busy} onClick={onReset} data-testid="buyer-closing-reset" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--text-muted)" }}><RotateCcw size={14} /> Use global checklist</button>
        )}
        <button type="button" className="btn-primary" disabled={busy} onClick={onSave} data-testid="buyer-closing-save" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Save size={15} /> Save checklist</button>
      </div>
    </div>
  );
}

